/**
 * Elite Batch 21 Part 2 — Support S1 remaining (6) + Support S2 Ticket Lifecycle (21)
 * Password reset helper, remote work FAQ bot, self-service password reset bot,
 * two-way SMS support bot, WhatsApp auto responder, WhatsApp support bot,
 * case closure with education, case status inquiry bot, escalation workflow,
 * first response template selector, Jira ticket to Slack alert, live chat routing bot,
 * live chat to ticket converter, multi-channel ticket aggregator, SLA breach alert,
 * SLA breach internal escalation, SLA breach warning system, support agent QA sampler,
 * support backlog reprioritizer, support metrics dashboard updater, support queue overflow
 * deflector, support ticket auto categorizer, support ticket duplicate detector,
 * support ticket received confirmation, support ticket resolved follow-up,
 * support ticket waiting on customer, ticket aging alert system.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 21 PART 2 — SUPPORT S1 REMAINING (6) + SUPPORT S2 TICKET LIFECYCLE (21)
// =============================================================================

export const ELITE_BATCH21_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1. PASSWORD RESET HELPER (8 nodes)
  // ===================================================================

  "Password Reset Helper": {
    summary:
      "Fires when a user requests a password reset via webhook, AI extracts identity details from the payload, verifies identity via Okta MFA challenge, branches on verification success, generates a secure reset token with expiry via runCode, emails the reset link with instructions, logs the reset attempt to Slack for security monitoring, and records an audit trail in Google Sheets.",
    nodes: [
      trigger("Webhook: User requests password reset", "webhook_app", {
        source: "support_portal",
        event: "password_reset.requested",
        howItStarts:
          "Fires when a user submits a password reset request through the support portal or self-service page, sending the raw request payload with user-provided identity details",
        setup:
          "Configure webhook endpoint for password reset requests. Set Okta API token for identity verification. Set OpenAI API key for extraction. Configure email sending credentials and Slack webhook URL for security logging.",
        inputFields: [],
      }),

      // 2. AI extract user identity details
      aiNode(
        "2",
        "AI: Extract user identity from reset request",
        280,
        "You are a security-focused identity extraction agent. From the incoming password reset request payload, extract the user email, username, full name, any security question answers provided, the IP address of the requester, and the user agent string. Return JSON: {user_email, username, full_name, security_answers, requester_ip, user_agent, account_identifier}",
        "Password reset request payload: {webhook_payload}. Extract all identity-related fields regardless of payload format or field naming conventions.",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "username", as: "username", type: "string" },
          { field: "full_name", as: "full_name", type: "string" },
          { field: "security_answers", as: "security_answers", type: "string" },
          { field: "requester_ip", as: "requester_ip", type: "string" },
          { field: "user_agent", as: "user_agent", type: "string" },
          {
            field: "account_identifier",
            as: "account_identifier",
            type: "string",
          },
        ],
      ),

      // 3. Okta verify identity via MFA
      n("3", "hitApi", "Okta: Verify identity via MFA challenge", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/authn",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "username", value: "{username}" },
            {
              key: "options",
              value:
                '{"multiOptionalFactorEnroll":false,"warnBeforePasswordExpired":true}',
            },
          ],
          variables: [
            { name: "status", customName: "auth_status", type: "string" },
            {
              name: "sessionToken",
              customName: "session_token",
              type: "string",
            },
            {
              name: "_embedded.user.id",
              customName: "okta_user_id",
              type: "string",
            },
            {
              name: "_embedded.user.profile.login",
              customName: "okta_login",
              type: "string",
            },
          ],
        },
      }),

      // 4. Condition: identity verified?
      n("4", "condition", "Identity verified via MFA?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "auth_status",
              operator: "equals",
              value: "MFA_REQUIRED",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. runCode generate secure reset token (True path)
      n(
        "5",
        "runCode",
        "Generate secure password reset token with expiry",
        1000,
        {
          runCodeDetails: {
            code: `const crypto = require('crypto');
const resetToken = crypto.randomBytes(32).toString('hex');
const tokenHash = crypto.createHash('sha256').update(resetToken).digest('hex');
const expiresAt = new Date(Date.now() + 30 * 60 * 1000).toISOString(); // 30 min expiry
const resetLink = 'https://app.yourcompany.com/reset-password?token=' + resetToken + '&user=' + encodeURIComponent(variables.okta_user_id);
output.reset_token = resetToken;
output.token_hash = tokenHash;
output.expires_at = expiresAt;
output.reset_link = resetLink;
output.token_valid_minutes = 30;`,
            outputVariables: [
              { name: "reset_token", type: "string" },
              { name: "token_hash", type: "string" },
              { name: "expires_at", type: "string" },
              { name: "reset_link", type: "string" },
              { name: "token_valid_minutes", type: "number" },
            ],
          },
        },
      ),

      // 6. Email send reset link
      emailNode(
        "6",
        "Email: Send password reset link with instructions",
        1240,
        "{user_email}",
        "Your Password Reset Request",
        "Hi {full_name},\n\nWe received your password reset request. Click the link below to reset your password:\n\n{reset_link}\n\nThis link expires in {token_valid_minutes} minutes. If you did not request this reset, please contact security immediately.\n\nFor your security:\n- Choose a password at least 12 characters long\n- Use a mix of uppercase, lowercase, numbers, and symbols\n- Do not reuse passwords from other accounts\n\nBest regards,\nSecurity Team",
      ),

      // 7. Slack log reset attempt for security team
      slackNode(
        "7",
        "Slack: Log reset attempt for security team",
        1480,
        ":lock: *Password Reset Attempt*\n- User: {full_name} ({user_email})\n- Username: {username}\n- Okta ID: {okta_user_id}\n- Auth Status: {auth_status}\n- Requester IP: {requester_ip}\n- User Agent: {user_agent}\n- Token Expires: {expires_at}",
      ),

      // 8. Google Sheets audit trail
      sheetsNode(
        "8",
        "Google Sheets: Log password reset audit trail",
        1720,
        "PasswordResetAudit",
        '[["{user_email}","{username}","{okta_user_id}","{auth_status}","{requester_ip}","{user_agent}","{expires_at}","{token_valid_minutes}","completed"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("4", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 2. REMOTE WORK POLICY FAQ BOT (7 nodes)
  // ===================================================================

  "Remote Work Policy FAQ Bot": {
    summary:
      "Fires when an employee submits a remote work policy question via webhook, AI extracts the question intent and jurisdiction context, searches Pinecone for relevant policy documents, AI generates a grounded answer citing policy sections, branches on jurisdiction-specific eligibility, sends the reply with policy citations via chat, and logs queries to Google Sheets for policy team review.",
    nodes: [
      trigger("Webhook: Employee asks remote work question", "webhook_app", {
        source: "slack_bot",
        event: "message.remote_work_question",
        howItStarts:
          "Fires when an employee submits a remote work or WFH policy question through the internal Slack bot or HR portal chat, sending the raw message payload",
        setup:
          "Configure Slack bot webhook for incoming questions. Set OpenAI API key for question analysis and answer generation. Configure Pinecone API key and index for policy document search. Set up chat reply channel.",
        inputFields: [],
      }),

      // 2. AI extract question intent and jurisdiction
      aiNode(
        "2",
        "AI: Extract question intent and jurisdiction context",
        280,
        "You are an HR policy question analyst. Extract the employee question intent, the specific policy area they are asking about (e.g., eligibility, equipment, stipend, tax implications, time zones), the jurisdiction or country context if mentioned, the employee role or department if provided, and any specific dates or scenarios referenced. Return JSON: {question_intent, policy_area, jurisdiction, employee_role, employee_department, specific_scenario, urgency_level, employee_name, employee_id}",
        "Employee remote work question payload: {webhook_payload}. Extract intent and context regardless of how the question is phrased.",
        [
          { field: "question_intent", as: "question_intent", type: "string" },
          { field: "policy_area", as: "policy_area", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "employee_role", as: "employee_role", type: "string" },
          {
            field: "employee_department",
            as: "employee_department",
            type: "string",
          },
          {
            field: "specific_scenario",
            as: "specific_scenario",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
        ],
      ),

      // 3. Pinecone search remote work policy documents
      n("3", "hitApi", "Pinecone: Search remote work policy documents", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "vector", value: "{question_embedding}" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "namespace", value: "remote-work-policies" },
            {
              key: "filter",
              value: '{"jurisdiction":{"$in":["{jurisdiction}","global"]}}',
            },
          ],
          variables: [
            {
              name: "matches[0].metadata.content",
              customName: "policy_doc_1",
              type: "string",
            },
            {
              name: "matches[0].metadata.section",
              customName: "policy_section_1",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "relevance_score_1",
              type: "number",
            },
            {
              name: "matches[1].metadata.content",
              customName: "policy_doc_2",
              type: "string",
            },
            {
              name: "matches[1].metadata.section",
              customName: "policy_section_2",
              type: "string",
            },
            {
              name: "matches[2].metadata.content",
              customName: "policy_doc_3",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI generate grounded answer with policy citations
      aiNode(
        "4",
        "AI: Generate grounded answer with policy citations",
        760,
        "You are a remote work policy expert. Generate a clear, helpful answer to the employee question using ONLY the provided policy documents. Always cite the specific policy section. If the answer depends on jurisdiction, explicitly state which jurisdiction rules apply and whether the employee needs to check with HR for their specific location. If the policy docs do not cover the question, say so clearly and recommend contacting HR directly. Return JSON: {answer, policy_sections_cited, jurisdiction_specific, eligibility_applies, confidence_level, escalate_to_hr, follow_up_needed}",
        "Employee question: {question_intent} | Policy area: {policy_area} | Jurisdiction: {jurisdiction} | Role: {employee_role} | Department: {employee_department} | Scenario: {specific_scenario} | Policy Doc 1 (Section: {policy_section_1}): {policy_doc_1} | Policy Doc 2: {policy_doc_2} | Policy Doc 3: {policy_doc_3}",
        [
          { field: "answer", as: "policy_answer", type: "string" },
          {
            field: "policy_sections_cited",
            as: "sections_cited",
            type: "string",
          },
          {
            field: "jurisdiction_specific",
            as: "jurisdiction_specific",
            type: "string",
          },
          {
            field: "eligibility_applies",
            as: "eligibility_applies",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "escalate_to_hr", as: "escalate_to_hr", type: "string" },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
        ],
      ),

      // 5. Condition: jurisdiction-specific eligibility applies?
      n("5", "condition", "Jurisdiction-specific eligibility applies?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "jurisdiction_specific",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. sendMessage reply with policy citation
      n(
        "6",
        "sendMessage",
        "Reply: Policy answer with jurisdiction-specific notes",
        1240,
        {
          sendMessageDetails: {
            message:
              "Hi {employee_name}, here is what our remote work policy says about your question:\n\n{policy_answer}\n\n*Policy Sections Referenced:* {sections_cited}\n*Jurisdiction:* {jurisdiction}\n*Eligibility:* {eligibility_applies}\n\nNote: This answer is specific to your jurisdiction ({jurisdiction}). Rules may differ in other locations. If you need further clarification, please reach out to your HR Business Partner.\n\nConfidence: {confidence_level}",
          },
        },
      ),

      // 7. Google Sheets log queries for policy team
      sheetsNode(
        "7",
        "Google Sheets: Log query for policy team review",
        1480,
        "RemoteWorkFAQ",
        '[["{employee_name}","{employee_id}","{question_intent}","{policy_area}","{jurisdiction}","{sections_cited}","{jurisdiction_specific}","{confidence_level}","{escalate_to_hr}","{follow_up_needed}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 3. SELF SERVICE PASSWORD RESET BOT (7 nodes)
  // ===================================================================

  "Self Service Password Reset Bot": {
    summary:
      "Fires when a user initiates a password reset in chat via webhook, AI extracts the identity claim from the message, verifies identity via Okta MFA challenge, branches on MFA verification result, executes the password reset in Active Directory, confirms reset success via chat message with security tips, and logs the reset event with verification method to Google Sheets.",
    nodes: [
      trigger("Webhook: User initiates password reset in chat", "webhook_app", {
        source: "teams_bot",
        event: "message.password_reset",
        howItStarts:
          "Fires when a user sends a password reset request through the Teams bot or internal chat system, sending the raw message payload with user context",
        setup:
          "Configure Teams bot webhook or chat system webhook for password reset requests. Set Okta API token for MFA verification. Configure Active Directory API credentials. Set OpenAI API key for identity extraction.",
        inputFields: [],
      }),

      // 2. AI extract identity claim from message
      aiNode(
        "2",
        "AI: Extract identity claim from user message",
        280,
        "You are an identity verification assistant. From the user chat message requesting a password reset, extract the claimed username, email address, employee ID, and any verification details provided (like manager name, department, last 4 of phone). Return JSON: {claimed_username, claimed_email, employee_id, verification_details, chat_user_id, chat_display_name, request_timestamp}",
        "Chat message payload: {webhook_payload}. Extract identity claim details regardless of how the user phrases their reset request.",
        [
          { field: "claimed_username", as: "claimed_username", type: "string" },
          { field: "claimed_email", as: "claimed_email", type: "string" },
          { field: "employee_id", as: "employee_id", type: "string" },
          {
            field: "verification_details",
            as: "verification_details",
            type: "string",
          },
          { field: "chat_user_id", as: "chat_user_id", type: "string" },
          {
            field: "chat_display_name",
            as: "chat_display_name",
            type: "string",
          },
          {
            field: "request_timestamp",
            as: "request_timestamp",
            type: "string",
          },
        ],
      ),

      // 3. Okta MFA challenge verification
      n("3", "hitApi", "Okta: Verify identity via MFA challenge", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{okta_domain}/api/v1/authn",
          headers: [
            { key: "Authorization", value: "SSWS {okta_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "username", value: "{claimed_username}" },
            {
              key: "options",
              value:
                '{"multiOptionalFactorEnroll":false,"warnBeforePasswordExpired":true}',
            },
          ],
          variables: [
            { name: "status", customName: "mfa_status", type: "string" },
            {
              name: "sessionToken",
              customName: "mfa_session_token",
              type: "string",
            },
            {
              name: "_embedded.user.id",
              customName: "okta_user_id",
              type: "string",
            },
            {
              name: "_embedded.user.profile.email",
              customName: "verified_email",
              type: "string",
            },
          ],
        },
      }),

      // 4. Condition: MFA verified?
      n("4", "condition", "MFA verification passed?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "mfa_status", operator: "equals", value: "SUCCESS" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Active Directory password reset (True path)
      n("5", "hitApi", "Active Directory: Execute password reset", 1000, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://graph.microsoft.com/v1.0/users/{okta_user_id}",
          headers: [
            { key: "Authorization", value: "Bearer {ms_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "passwordProfile",
              value:
                '{"forceChangePasswordNextSignIn":true,"password":"TempReset!{random_suffix}"}',
            },
          ],
          variables: [
            { name: "id", customName: "ad_user_id", type: "string" },
            { name: "userPrincipalName", customName: "ad_upn", type: "string" },
          ],
        },
      }),

      // 6. sendMessage confirm reset success
      n(
        "6",
        "sendMessage",
        "Chat: Confirm password reset with security tips",
        1240,
        {
          sendMessageDetails: {
            message:
              "Your password has been reset successfully, {chat_display_name}.\n\nA temporary password has been sent to your verified email ({verified_email}). You will be required to change it on your next login.\n\nSecurity Tips:\n- Choose a strong password (12+ characters with mixed case, numbers, symbols)\n- Never share your password with anyone, including IT staff\n- Enable MFA on all your accounts\n- If you did not request this reset, contact IT Security immediately\n\nVerification method: Okta MFA\nReset completed at: {request_timestamp}",
          },
        },
      ),

      // 7. Google Sheets log reset event
      sheetsNode(
        "7",
        "Google Sheets: Log reset event with verification method",
        1480,
        "PasswordResetLog",
        '[["{claimed_username}","{verified_email}","{employee_id}","{mfa_status}","{okta_user_id}","Okta MFA","{chat_user_id}","{request_timestamp}","success"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("4", "6", "output_2"),
    ],
  },

  // ===================================================================
  // 4. TWO-WAY SMS CUSTOMER SUPPORT BOT (8 nodes)
  // ===================================================================

  "Two-Way SMS Customer Support Bot": {
    summary:
      "Fires when an inbound SMS arrives via Twilio webhook, AI extracts customer intent from the SMS, HubSpot looks up the customer record and recent orders, AI generates a concise SMS-friendly response under 160 characters, branches on whether a human agent is needed, sends the SMS response via Twilio, notifies Slack support queue if escalation is required, and logs the conversation to Google Sheets.",
    nodes: [
      trigger("Webhook: Inbound SMS received via Twilio", "webhook_app", {
        source: "twilio",
        event: "sms.inbound",
        howItStarts:
          "Fires when a customer sends an inbound SMS to your Twilio number, delivering the raw SMS payload with sender phone number, message body, and message metadata",
        setup:
          "Configure Twilio webhook for inbound SMS messages. Set Twilio Account SID and Auth Token for sending replies. Set HubSpot token for customer lookup. Set OpenAI API key for intent classification and response generation. Configure Slack webhook for escalations.",
        inputFields: [],
      }),

      // 2. AI extract customer intent from SMS
      aiNode(
        "2",
        "AI: Extract customer intent from SMS",
        280,
        "You are an SMS support intent classifier. From the inbound SMS payload, extract the sender phone number, the message body, the customer intent (order status, return request, billing question, technical issue, general inquiry, complaint, other), urgency level, and any order numbers or account references mentioned. Return JSON: {sender_phone, message_body, customer_intent, urgency, order_number, account_reference, sentiment, language}",
        "Inbound SMS payload: {webhook_payload}. Extract customer intent and details from the SMS body regardless of abbreviations, typos, or informal language.",
        [
          { field: "sender_phone", as: "sender_phone", type: "string" },
          { field: "message_body", as: "message_body", type: "string" },
          { field: "customer_intent", as: "customer_intent", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "order_number", as: "order_number", type: "string" },
          {
            field: "account_reference",
            as: "account_reference",
            type: "string",
          },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "language", as: "language", type: "string" },
        ],
      ),

      // 3. HubSpot lookup customer record
      n(
        "3",
        "hitApi",
        "HubSpot: Lookup customer record and recent orders",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"phone","operator":"CONTAINS_TOKEN","value":"{sender_phone}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","phone","hs_object_id","lifecyclestage","recent_deal_amount","notes_last_contacted"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "customer_first_name",
                type: "string",
              },
              {
                name: "results[0].properties.lastname",
                customName: "customer_last_name",
                type: "string",
              },
              {
                name: "results[0].properties.email",
                customName: "customer_email",
                type: "string",
              },
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "hubspot_contact_id",
                type: "string",
              },
              {
                name: "results[0].properties.recent_deal_amount",
                customName: "recent_deal_amount",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI generate concise SMS-friendly response
      aiNode(
        "4",
        "AI: Generate SMS-friendly response (under 160 chars)",
        760,
        "You are an SMS customer support agent. Generate a concise, helpful SMS response that fits within 160 characters. Be friendly but brief. If the customer has a name on file, use it. Include relevant information based on their intent. If the issue requires a human agent (complaints, complex billing, technical issues), set requires_human to true and write a holding message. Return JSON: {sms_response, requires_human, response_category, confidence}",
        "Customer: {customer_first_name} {customer_last_name} | Phone: {sender_phone} | Intent: {customer_intent} | Message: {message_body} | Order #: {order_number} | Urgency: {urgency} | Sentiment: {sentiment} | Lifecycle: {lifecycle_stage}",
        [
          { field: "sms_response", as: "sms_response", type: "string" },
          { field: "requires_human", as: "requires_human", type: "string" },
          {
            field: "response_category",
            as: "response_category",
            type: "string",
          },
          { field: "confidence", as: "response_confidence", type: "string" },
        ],
      ),

      // 5. Condition: requires human agent?
      n("5", "condition", "Requires human agent escalation?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "requires_human", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. SMS send response via Twilio (both paths send SMS)
      n("6", "hitApi", "Twilio: Send SMS response to customer", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.twilio.com/2010-04-01/Accounts/{twilio_account_sid}/Messages.json",
          headers: [
            { key: "Authorization", value: "Basic {twilio_auth_base64}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "To", value: "{sender_phone}" },
            { key: "From", value: "{twilio_phone_number}" },
            { key: "Body", value: "{sms_response}" },
          ],
          variables: [
            { name: "sid", customName: "message_sid", type: "string" },
            { name: "status", customName: "sms_status", type: "string" },
          ],
        },
      }),

      // 7. Slack notify support queue if escalation (True path)
      slackNode(
        "7",
        "Slack: Notify support queue for escalation",
        1240,
        ":rotating_light: *SMS Escalation Needed*\n- Customer: {customer_first_name} {customer_last_name}\n- Phone: {sender_phone}\n- Intent: {customer_intent}\n- Message: {message_body}\n- Urgency: {urgency}\n- Sentiment: {sentiment}\n- HubSpot ID: {hubspot_contact_id}\n- Order #: {order_number}\nPlease respond within 10 minutes.",
        300,
      ),

      // 8. Google Sheets log conversation
      sheetsNode(
        "8",
        "Google Sheets: Log SMS conversation",
        1480,
        "SMSSupportLog",
        '[["{sender_phone}","{customer_first_name} {customer_last_name}","{customer_intent}","{message_body}","{sms_response}","{requires_human}","{response_category}","{sms_status}","{message_sid}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("5", "7", "output_1"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 5. WHATSAPP BUSINESS AUTO RESPONDER (8 nodes)
  // ===================================================================

  "WhatsApp Business Auto Responder": {
    summary:
      "Fires when an inbound WhatsApp message arrives via the WhatsApp Business API, AI classifies the message type (text, image, voice, location), AI determines intent and generates an appropriate response, branches on whether an approved WhatsApp template is required for outbound, runCode selects the correct approved message template, sends the response via WhatsApp Business API, Slack flags rich media messages for review, and logs the conversation to Google Sheets.",
    nodes: [
      trigger("Webhook: Inbound WhatsApp message received", "webhook_app", {
        source: "whatsapp_business",
        event: "messages",
        howItStarts:
          "Fires when a customer sends a message through WhatsApp Business API, delivering the raw payload with sender info, message type (text, image, voice, location), and message content",
        setup:
          "Configure WhatsApp Business API webhook for incoming messages. Set OpenAI API key for message classification and response generation. Set WhatsApp Business API token for sending replies. Configure Slack webhook for media review alerts. Configure Google Sheets for logging.",
        inputFields: [],
      }),

      // 2. AI classify message type
      aiNode(
        "2",
        "AI: Classify WhatsApp message type and content",
        280,
        "You are a WhatsApp message classifier. From the incoming WhatsApp Business API payload, identify the message type (text, image, voice, location, document, contact, sticker), extract the sender phone and display name, the message content or media URL, any caption text, and the conversation context. Return JSON: {sender_phone, sender_name, message_type, message_content, media_url, caption, wa_message_id, conversation_id, is_rich_media, timestamp}",
        "WhatsApp incoming message payload: {webhook_payload}. Classify the message type and extract all relevant fields regardless of the WhatsApp API version used.",
        [
          { field: "sender_phone", as: "wa_sender_phone", type: "string" },
          { field: "sender_name", as: "wa_sender_name", type: "string" },
          { field: "message_type", as: "wa_message_type", type: "string" },
          {
            field: "message_content",
            as: "wa_message_content",
            type: "string",
          },
          { field: "media_url", as: "wa_media_url", type: "string" },
          { field: "caption", as: "wa_caption", type: "string" },
          { field: "wa_message_id", as: "wa_message_id", type: "string" },
          {
            field: "conversation_id",
            as: "wa_conversation_id",
            type: "string",
          },
          { field: "is_rich_media", as: "is_rich_media", type: "string" },
          { field: "timestamp", as: "wa_timestamp", type: "string" },
        ],
      ),

      // 3. AI determine intent and generate response
      aiNode(
        "3",
        "AI: Determine intent and generate WhatsApp response",
        520,
        "You are a WhatsApp customer support agent. Based on the message type and content, determine the customer intent, generate an appropriate response, and decide whether the response needs to use a pre-approved WhatsApp template (required for messages outside the 24-hour conversation window). Keep responses concise and WhatsApp-friendly. Return JSON: {customer_intent, response_text, needs_template, template_name, template_language, urgency, sentiment, auto_resolvable}",
        "Sender: {wa_sender_name} ({wa_sender_phone}) | Type: {wa_message_type} | Content: {wa_message_content} | Caption: {wa_caption} | Conversation: {wa_conversation_id}",
        [
          { field: "customer_intent", as: "wa_intent", type: "string" },
          { field: "response_text", as: "wa_response_text", type: "string" },
          { field: "needs_template", as: "needs_template", type: "string" },
          { field: "template_name", as: "template_name", type: "string" },
          {
            field: "template_language",
            as: "template_language",
            type: "string",
          },
          { field: "urgency", as: "wa_urgency", type: "string" },
          { field: "sentiment", as: "wa_sentiment", type: "string" },
          {
            field: "auto_resolvable",
            as: "wa_auto_resolvable",
            type: "string",
          },
        ],
      ),

      // 4. Condition: requires approved WhatsApp template?
      n("4", "condition", "Requires approved WhatsApp message template?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "needs_template", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. runCode select approved message template (True path)
      n("5", "runCode", "Select approved WhatsApp message template", 1000, {
        runCodeDetails: {
          code: `const templateMap = {
  'order_update': { name: 'order_status_update', namespace: 'default', components: [{ type: 'body', parameters: [{ type: 'text', text: variables.wa_sender_name }, { type: 'text', text: variables.wa_response_text }] }] },
  'support_reply': { name: 'support_response', namespace: 'default', components: [{ type: 'body', parameters: [{ type: 'text', text: variables.wa_sender_name }, { type: 'text', text: variables.wa_response_text }] }] },
  'greeting': { name: 'welcome_message', namespace: 'default', components: [{ type: 'body', parameters: [{ type: 'text', text: variables.wa_sender_name }] }] },
};
const selected = templateMap[variables.template_name] || templateMap['support_reply'];
output.template_payload = JSON.stringify(selected);
output.selected_template_name = selected.name;
output.template_namespace = selected.namespace;`,
          outputVariables: [
            { name: "template_payload", type: "string" },
            { name: "selected_template_name", type: "string" },
            { name: "template_namespace", type: "string" },
          ],
        },
      }),

      // 6. WhatsApp Business API send response
      n("6", "hitApi", "WhatsApp: Send response via Business API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_number_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{wa_sender_phone}" },
            { key: "type", value: "text" },
            { key: "text", value: '{"body":"{wa_response_text}"}' },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "sent_message_id",
              type: "string",
            },
            {
              name: "contacts[0].wa_id",
              customName: "recipient_wa_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Slack flag rich media messages for review
      slackNode(
        "7",
        "Slack: Flag rich media messages for review",
        1480,
        ":camera: *WhatsApp Rich Media Message*\n- From: {wa_sender_name} ({wa_sender_phone})\n- Type: {wa_message_type}\n- Media URL: {wa_media_url}\n- Caption: {wa_caption}\n- Intent: {wa_intent}\n- Sentiment: {wa_sentiment}\nReview needed for non-text content.",
      ),

      // 8. Google Sheets log conversation
      sheetsNode(
        "8",
        "Google Sheets: Log WhatsApp conversation",
        1720,
        "WhatsAppLog",
        '[["{wa_sender_phone}","{wa_sender_name}","{wa_message_type}","{wa_message_content}","{wa_intent}","{wa_response_text}","{needs_template}","{wa_urgency}","{wa_sentiment}","{sent_message_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("4", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 6. WHATSAPP SUPPORT BOT (8 nodes)
  // ===================================================================

  "WhatsApp Support Bot": {
    summary:
      "Fires when a WhatsApp message with possible media arrives via webhook, AI processes text/image/voice/location inputs and extracts intent, HubSpot looks up customer context and ticket history, AI generates a response grounded in knowledge base content, branches on confidence and sensitivity, sends the response via WhatsApp Business API, creates a Jira ticket with media references for agent handoff on low-confidence or sensitive queries, and logs the interaction to Google Sheets.",
    nodes: [
      trigger("Webhook: WhatsApp message with possible media", "webhook_app", {
        source: "whatsapp_business",
        event: "messages",
        howItStarts:
          "Fires when a customer sends any type of WhatsApp message (text, image, voice note, location share, document) to the support number, delivering the full media-enriched payload",
        setup:
          "Configure WhatsApp Business API webhook for all message types. Set OpenAI API key with vision capabilities for image processing. Set HubSpot token for customer lookup. Configure Jira API credentials for ticket creation. Set WhatsApp Business API token for sending replies.",
        inputFields: [],
      }),

      // 2. AI process multi-modal inputs and extract intent
      aiNode(
        "2",
        "AI: Process text/image/voice/location and extract intent",
        280,
        "You are a multi-modal WhatsApp support agent. Process the incoming message which may contain text, image descriptions, voice transcription, or location data. Extract the customer phone, name, message type, the actual content or description, the customer intent, any product or order references, and whether the issue is sensitive (billing disputes, account security, personal data requests). Return JSON: {sender_phone, sender_name, message_type, content_summary, customer_intent, product_reference, order_reference, is_sensitive, media_description, urgency, language}",
        "WhatsApp message payload: {webhook_payload}. Process all message types and extract intent regardless of format. For images, describe what was shared. For voice, use any transcription available. For location, note the coordinates.",
        [
          { field: "sender_phone", as: "wa_phone", type: "string" },
          { field: "sender_name", as: "wa_name", type: "string" },
          { field: "message_type", as: "msg_type", type: "string" },
          { field: "content_summary", as: "content_summary", type: "string" },
          { field: "customer_intent", as: "cust_intent", type: "string" },
          { field: "product_reference", as: "product_ref", type: "string" },
          { field: "order_reference", as: "order_ref", type: "string" },
          { field: "is_sensitive", as: "is_sensitive", type: "string" },
          { field: "media_description", as: "media_desc", type: "string" },
          { field: "urgency", as: "msg_urgency", type: "string" },
          { field: "language", as: "msg_language", type: "string" },
        ],
      ),

      // 3. HubSpot lookup customer context and ticket history
      n(
        "3",
        "hitApi",
        "HubSpot: Lookup customer context and ticket history",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"phone","operator":"CONTAINS_TOKEN","value":"{wa_phone}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["firstname","lastname","email","phone","hs_object_id","num_associated_deals","notes_last_contacted","hs_lead_status"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.firstname",
                customName: "cust_firstname",
                type: "string",
              },
              {
                name: "results[0].properties.lastname",
                customName: "cust_lastname",
                type: "string",
              },
              {
                name: "results[0].properties.email",
                customName: "cust_email",
                type: "string",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "hubspot_id",
                type: "string",
              },
              {
                name: "results[0].properties.num_associated_deals",
                customName: "deal_count",
                type: "string",
              },
              {
                name: "results[0].properties.notes_last_contacted",
                customName: "last_contacted",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. AI generate response with knowledge base grounding
      aiNode(
        "4",
        "AI: Generate KB-grounded response",
        760,
        "You are a knowledgeable WhatsApp support bot. Generate a helpful response grounded in your knowledge base. Consider the customer history, their intent, and the message type. For sensitive topics (billing, security, personal data), recommend speaking to a human agent. For media messages, acknowledge what was shared. Rate your confidence from 0 to 1. Return JSON: {response_text, confidence, kb_articles_referenced, recommended_action, needs_agent_handoff, response_tone, follow_up_question}",
        "Customer: {cust_firstname} {cust_lastname} | Phone: {wa_phone} | Intent: {cust_intent} | Message Type: {msg_type} | Content: {content_summary} | Media: {media_desc} | Product: {product_ref} | Order: {order_ref} | Sensitive: {is_sensitive} | Deals: {deal_count} | Last Contact: {last_contacted} | Urgency: {msg_urgency} | Language: {msg_language}",
        [
          { field: "response_text", as: "bot_response", type: "string" },
          { field: "confidence", as: "response_confidence", type: "number" },
          {
            field: "kb_articles_referenced",
            as: "kb_articles",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "needs_agent_handoff", as: "needs_handoff", type: "string" },
          { field: "response_tone", as: "response_tone", type: "string" },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
        ],
      ),

      // 5. Condition: confidence > 0.8 and not sensitive?
      n("5", "condition", "Confidence > 0.8 and not sensitive?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "response_confidence",
              operator: "greater_than",
              value: "0.8",
            },
            { variable: "is_sensitive", operator: "equals", value: "false" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. WhatsApp send response (True path)
      n("6", "hitApi", "WhatsApp: Send auto-response via Business API", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.facebook.com/v18.0/{whatsapp_phone_number_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {whatsapp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "messaging_product", value: "whatsapp" },
            { key: "to", value: "{wa_phone}" },
            { key: "type", value: "text" },
            { key: "text", value: '{"body":"{bot_response}"}' },
          ],
          variables: [
            {
              name: "messages[0].id",
              customName: "wa_sent_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Jira create ticket for agent handoff (False path)
      n(
        "7",
        "hitApi",
        "Jira: Create ticket with media refs for agent handoff",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"SUP"},"summary":"WhatsApp handoff: {cust_intent} — {wa_name}","issuetype":{"name":"Task"},"priority":{"name":"High"},"description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Customer: {cust_firstname} {cust_lastname} ({wa_phone})\\nIntent: {cust_intent}\\nMessage Type: {msg_type}\\nContent: {content_summary}\\nMedia: {media_desc}\\nSensitive: {is_sensitive}\\nConfidence: {response_confidence}\\nKB Articles: {kb_articles}\\nRecommended Action: {recommended_action}"}]}]},"labels":["whatsapp","agent-handoff","{msg_urgency}"]}',
              },
            ],
            variables: [
              { name: "id", customName: "jira_ticket_id", type: "string" },
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
        300,
      ),

      // 8. Google Sheets log interaction
      sheetsNode(
        "8",
        "Google Sheets: Log WhatsApp support interaction",
        1480,
        "WhatsAppSupportLog",
        '[["{wa_phone}","{wa_name}","{msg_type}","{cust_intent}","{content_summary}","{response_confidence}","{is_sensitive}","{needs_handoff}","{bot_response}","{jira_ticket_key}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 7. CASE CLOSURE WITH EDUCATION (8 nodes)
  // ===================================================================

  "Case Closure with Education": {
    summary:
      "Fires when a support case is marked resolved in Zendesk, AI extracts resolution details from the ticket, AI generates a resolution summary matched to relevant KB articles, emails the customer with the summary and reopen option, waits 48 hours, checks if the ticket was reopened, AI analyzes the reopen reason for improvement insights, and logs closure effectiveness to Google Sheets.",
    nodes: [
      trigger(
        "Webhook: Support case marked resolved in Zendesk",
        "webhook_app",
        {
          source: "zendesk",
          event: "ticket.status.solved",
          howItStarts:
            "Fires when a Zendesk ticket status changes to solved/resolved, sending the ticket payload with resolution details, customer info, and agent notes",
          setup:
            "Configure Zendesk webhook trigger for ticket solved events. Set OpenAI API key for resolution summarization and article matching. Configure email credentials for customer communication. Set Zendesk API token for ticket status checks.",
          inputFields: [],
        },
      ),

      // 2. AI extract resolution details from ticket
      aiNode(
        "2",
        "AI: Extract resolution details from ticket",
        280,
        "You are a support resolution analyst. From the Zendesk ticket solved webhook payload, extract the ticket ID, customer email, customer name, the resolution description, the agent who resolved it, the product/feature involved, the root cause, and the resolution method. Return JSON: {ticket_id, customer_email, customer_name, resolution_description, resolving_agent, product_area, root_cause, resolution_method, ticket_category, time_to_resolve}",
        "Zendesk ticket solved payload: {webhook_payload}. Extract all resolution details regardless of how the agent documented the resolution.",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          {
            field: "resolution_description",
            as: "resolution_description",
            type: "string",
          },
          { field: "resolving_agent", as: "resolving_agent", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
          { field: "root_cause", as: "root_cause", type: "string" },
          {
            field: "resolution_method",
            as: "resolution_method",
            type: "string",
          },
          { field: "ticket_category", as: "ticket_category", type: "string" },
          { field: "time_to_resolve", as: "time_to_resolve", type: "string" },
        ],
      ),

      // 3. AI generate resolution summary and match KB articles
      aiNode(
        "3",
        "AI: Generate resolution summary and match KB articles",
        520,
        "You are a customer education specialist. Create a clear, friendly resolution summary for the customer that explains what happened and how it was resolved. Match 2-3 relevant knowledge base articles that would help the customer avoid this issue in the future or learn more about the feature. Return JSON: {resolution_summary, kb_article_1_title, kb_article_1_url, kb_article_2_title, kb_article_2_url, kb_article_3_title, kb_article_3_url, prevention_tips, reopen_instructions}",
        "Ticket: {ticket_id} | Category: {ticket_category} | Product: {product_area} | Root Cause: {root_cause} | Resolution: {resolution_description} | Method: {resolution_method} | Customer: {customer_name}",
        [
          {
            field: "resolution_summary",
            as: "resolution_summary",
            type: "string",
          },
          {
            field: "kb_article_1_title",
            as: "kb_article_1_title",
            type: "string",
          },
          { field: "kb_article_1_url", as: "kb_article_1_url", type: "string" },
          {
            field: "kb_article_2_title",
            as: "kb_article_2_title",
            type: "string",
          },
          { field: "kb_article_2_url", as: "kb_article_2_url", type: "string" },
          {
            field: "kb_article_3_title",
            as: "kb_article_3_title",
            type: "string",
          },
          { field: "kb_article_3_url", as: "kb_article_3_url", type: "string" },
          { field: "prevention_tips", as: "prevention_tips", type: "string" },
          {
            field: "reopen_instructions",
            as: "reopen_instructions",
            type: "string",
          },
        ],
      ),

      // 4. Email customer with resolution summary
      emailNode(
        "4",
        "Email: Send resolution summary with help articles",
        760,
        "{customer_email}",
        "Your support case #{ticket_id} has been resolved",
        "Hi {customer_name},\n\nGreat news — your support case #{ticket_id} has been resolved.\n\n**What happened:**\n{resolution_summary}\n\n**Helpful articles to prevent this in the future:**\n1. {kb_article_1_title}: {kb_article_1_url}\n2. {kb_article_2_title}: {kb_article_2_url}\n3. {kb_article_3_title}: {kb_article_3_url}\n\n**Tips:**\n{prevention_tips}\n\n**Need to reopen this case?**\n{reopen_instructions}\n\nSimply reply to this email or visit your support portal to reopen this ticket if the issue persists.\n\nBest regards,\nSupport Team",
      ),

      // 5. Delay 48 hours
      n("5", "delayAction", "Wait 48 hours for customer feedback", 1000, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      // 6. Condition: ticket reopened?
      n("6", "condition", "Ticket reopened within 48 hours?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "ticket_status", operator: "equals", value: "open" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 7. AI analyze reopen reason (True path — reopened)
      aiNode(
        "7",
        "AI: Analyze reopen reason for improvement insights",
        1480,
        "You are a support quality analyst. The customer reopened their ticket after resolution. Analyze why: was the original resolution incomplete, did the customer misunderstand the fix, did a new related issue arise, or was the root cause not properly addressed? Provide improvement recommendations for the support team. Return JSON: {reopen_reason, original_resolution_quality, improvement_recommendation, training_needed, process_gap, severity_of_reopen}",
        "Original ticket: {ticket_id} | Category: {ticket_category} | Original resolution: {resolution_description} | Root cause: {root_cause} | Product: {product_area} | Agent: {resolving_agent} | Time to resolve: {time_to_resolve}",
        [
          { field: "reopen_reason", as: "reopen_reason", type: "string" },
          {
            field: "original_resolution_quality",
            as: "resolution_quality",
            type: "string",
          },
          {
            field: "improvement_recommendation",
            as: "improvement_rec",
            type: "string",
          },
          { field: "training_needed", as: "training_needed", type: "string" },
          { field: "process_gap", as: "process_gap", type: "string" },
          {
            field: "severity_of_reopen",
            as: "reopen_severity",
            type: "string",
          },
        ],
      ),

      // 8. Google Sheets log closure effectiveness
      sheetsNode(
        "8",
        "Google Sheets: Log closure effectiveness and article usefulness",
        1720,
        "CaseClosureEffectiveness",
        '[["{ticket_id}","{customer_email}","{ticket_category}","{product_area}","{resolution_method}","{time_to_resolve}","{kb_article_1_title}","{kb_article_2_title}","{reopen_reason}","{resolution_quality}","{improvement_rec}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
      e("6", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 8. CASE STATUS INQUIRY BOT (7 nodes)
  // ===================================================================

  "Case Status Inquiry Bot": {
    summary:
      "Fires when a customer asks about their ticket status via webhook, AI extracts the ticket reference from the message, Zendesk API fetches the ticket details securely, runCode formats the status with ETA language, branches on whether the ticket was found, sends a chat reply with status and next steps, and logs inquiry volume to Google Sheets.",
    nodes: [
      trigger("Webhook: Customer asks about ticket status", "webhook_app", {
        source: "chat_widget",
        event: "message.status_inquiry",
        howItStarts:
          "Fires when a customer sends a message asking about the status of their support ticket through the chat widget or support portal",
        setup:
          "Configure chat widget webhook for incoming messages. Set OpenAI API key for ticket reference extraction. Set Zendesk API token for ticket lookups. Configure chat reply endpoint.",
        inputFields: [],
      }),

      // 2. AI extract ticket reference from message
      aiNode(
        "2",
        "AI: Extract ticket reference from customer message",
        280,
        "You are a ticket reference extractor. From the customer message, find the ticket number, case ID, or reference number they are asking about. Also extract the customer email or name if provided. Handle various formats: #12345, ticket 12345, case-12345, ref:12345, etc. Return JSON: {ticket_number, customer_email, customer_name, inquiry_type, chat_session_id}",
        "Customer status inquiry message: {webhook_payload}. Find the ticket reference regardless of format or phrasing used by the customer.",
        [
          { field: "ticket_number", as: "ticket_number", type: "string" },
          { field: "customer_email", as: "inquiry_email", type: "string" },
          { field: "customer_name", as: "inquiry_name", type: "string" },
          { field: "inquiry_type", as: "inquiry_type", type: "string" },
          { field: "chat_session_id", as: "chat_session_id", type: "string" },
        ],
      ),

      // 3. Zendesk fetch ticket details
      n("3", "hitApi", "Zendesk: Fetch ticket details securely", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_number}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "ticket.id", customName: "zd_ticket_id", type: "string" },
            {
              name: "ticket.status",
              customName: "zd_ticket_status",
              type: "string",
            },
            {
              name: "ticket.subject",
              customName: "zd_ticket_subject",
              type: "string",
            },
            {
              name: "ticket.priority",
              customName: "zd_ticket_priority",
              type: "string",
            },
            {
              name: "ticket.assignee_id",
              customName: "zd_assignee_id",
              type: "string",
            },
            {
              name: "ticket.updated_at",
              customName: "zd_last_updated",
              type: "string",
            },
            {
              name: "ticket.created_at",
              customName: "zd_created_at",
              type: "string",
            },
          ],
        },
      }),

      // 4. runCode format status with ETA
      n("4", "runCode", "Format status with ETA language templates", 760, {
        runCodeDetails: {
          code: `const status = variables.zd_ticket_status || 'not_found';
const priority = variables.zd_ticket_priority || 'normal';
const etaMap = { 'urgent': '2-4 hours', 'high': '4-8 hours', 'normal': '1-2 business days', 'low': '3-5 business days' };
const statusMessages = {
  'new': 'Your ticket has been received and is in the queue for assignment.',
  'open': 'Your ticket is actively being worked on by our support team.',
  'pending': 'We are waiting for additional information to proceed with your ticket.',
  'hold': 'Your ticket is on hold while we investigate with our engineering team.',
  'solved': 'Your ticket has been resolved. Please reopen it if the issue persists.',
  'closed': 'Your ticket has been closed. Please open a new ticket if you need further help.',
  'not_found': 'We could not find a ticket with that reference number.',
};
const eta = etaMap[priority] || '1-2 business days';
output.status_message = statusMessages[status] || 'Your ticket status is: ' + status;
output.estimated_eta = eta;
output.ticket_found = status !== 'not_found' ? 'true' : 'false';
output.next_steps = status === 'pending' ? 'Please reply with the requested information to move your ticket forward.' : status === 'solved' ? 'Reply to reopen if the issue persists.' : 'Our team will update you as soon as there is progress.';
output.formatted_update_time = variables.zd_last_updated ? new Date(variables.zd_last_updated).toLocaleString() : 'N/A';`,
          outputVariables: [
            { name: "status_message", type: "string" },
            { name: "estimated_eta", type: "string" },
            { name: "ticket_found", type: "string" },
            { name: "next_steps", type: "string" },
            { name: "formatted_update_time", type: "string" },
          ],
        },
      }),

      // 5. Condition: ticket found?
      n("5", "condition", "Ticket found in Zendesk?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "ticket_found", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. sendMessage reply with status (both paths lead here with different content)
      n(
        "6",
        "sendMessage",
        "Reply: Ticket status with next steps and ETA",
        1240,
        {
          sendMessageDetails: {
            message:
              "Hi {inquiry_name}, here is the status of your ticket #{ticket_number}:\n\n**Subject:** {zd_ticket_subject}\n**Status:** {status_message}\n**Priority:** {zd_ticket_priority}\n**Estimated response time:** {estimated_eta}\n**Last updated:** {formatted_update_time}\n\n**Next steps:** {next_steps}\n\nIf you have additional details to share, you can reply here and we will update your ticket.",
          },
        },
      ),

      // 7. Google Sheets log inquiry volume
      sheetsNode(
        "7",
        "Google Sheets: Log status inquiry volume",
        1480,
        "StatusInquiryLog",
        '[["{ticket_number}","{inquiry_email}","{inquiry_type}","{zd_ticket_status}","{zd_ticket_priority}","{ticket_found}","{estimated_eta}","{chat_session_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 9. ESCALATION WORKFLOW (8 nodes)
  // ===================================================================

  "Escalation Workflow": {
    summary:
      "Fires when an agent triggers an escalation in Zendesk, AI extracts the escalation reason and builds a context package, Zendesk fetches full ticket history and prior touchpoints, runCode compiles a handoff summary so the escalated agent does not re-read history, branches on VIP customer status, Slack alerts the escalation team with the context package, emails the customer about the escalation with new contact info, and logs escalation metrics to Google Sheets.",
    nodes: [
      trigger("Webhook: Agent triggers escalation in Zendesk", "webhook_app", {
        source: "zendesk",
        event: "ticket.escalated",
        howItStarts:
          "Fires when a support agent triggers an escalation action on a Zendesk ticket, sending the ticket payload with escalation reason, current agent, and ticket metadata",
        setup:
          "Configure Zendesk webhook for escalation events. Set OpenAI API key for context extraction and summary generation. Set Zendesk API token for ticket history retrieval. Configure Slack webhook for escalation team alerts. Set email credentials.",
        inputFields: [],
      }),

      // 2. AI extract escalation reason and context
      aiNode(
        "2",
        "AI: Extract escalation reason and build context package",
        280,
        "You are an escalation analyst. From the Zendesk escalation event payload, extract the ticket ID, customer name, customer email, the escalation reason, the original agent, the customer tier (VIP, enterprise, standard), the number of prior escalations, the issue category, and the current resolution attempt summary. Return JSON: {ticket_id, customer_name, customer_email, escalation_reason, original_agent, customer_tier, prior_escalations, issue_category, resolution_attempts, urgency_level, customer_sentiment}",
        "Zendesk escalation event payload: {webhook_payload}. Extract all escalation context regardless of how the agent documented the escalation reason.",
        [
          { field: "ticket_id", as: "esc_ticket_id", type: "string" },
          { field: "customer_name", as: "esc_customer_name", type: "string" },
          { field: "customer_email", as: "esc_customer_email", type: "string" },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
          { field: "original_agent", as: "original_agent", type: "string" },
          { field: "customer_tier", as: "customer_tier", type: "string" },
          {
            field: "prior_escalations",
            as: "prior_escalations",
            type: "string",
          },
          { field: "issue_category", as: "esc_issue_category", type: "string" },
          {
            field: "resolution_attempts",
            as: "resolution_attempts",
            type: "string",
          },
          { field: "urgency_level", as: "esc_urgency", type: "string" },
          { field: "customer_sentiment", as: "esc_sentiment", type: "string" },
        ],
      ),

      // 3. Zendesk fetch full ticket history
      n(
        "3",
        "hitApi",
        "Zendesk: Fetch full ticket history and prior touchpoints",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{esc_ticket_id}/comments.json?sort_order=asc",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "comments[0].body",
                customName: "first_comment",
                type: "string",
              },
              {
                name: "comments[0].created_at",
                customName: "first_comment_date",
                type: "string",
              },
              { name: "count", customName: "total_comments", type: "number" },
            ],
          },
        },
      ),

      // 4. runCode compile handoff summary
      n("4", "runCode", "Compile handoff summary for escalated agent", 760, {
        runCodeDetails: {
          code: `const summary = [];
summary.push('=== ESCALATION HANDOFF SUMMARY ===');
summary.push('Ticket: #' + variables.esc_ticket_id);
summary.push('Customer: ' + variables.esc_customer_name + ' (' + variables.esc_customer_email + ')');
summary.push('Tier: ' + variables.customer_tier);
summary.push('Sentiment: ' + variables.esc_sentiment);
summary.push('');
summary.push('ESCALATION REASON: ' + variables.escalation_reason);
summary.push('ISSUE CATEGORY: ' + variables.esc_issue_category);
summary.push('URGENCY: ' + variables.esc_urgency);
summary.push('PRIOR ESCALATIONS: ' + variables.prior_escalations);
summary.push('');
summary.push('WHAT HAS BEEN TRIED:');
summary.push(variables.resolution_attempts);
summary.push('');
summary.push('ORIGINAL AGENT: ' + variables.original_agent);
summary.push('TOTAL COMMENTS: ' + variables.total_comments);
summary.push('FIRST CONTACT: ' + variables.first_comment_date);
summary.push('');
summary.push('ACTION NEEDED: Review context above and engage customer without asking them to repeat information.');
output.handoff_summary = summary.join('\\n');
output.is_vip = (variables.customer_tier === 'VIP' || variables.customer_tier === 'enterprise') ? 'true' : 'false';
output.escalation_priority = variables.esc_urgency === 'critical' ? 'P1' : variables.esc_urgency === 'high' ? 'P2' : 'P3';`,
          outputVariables: [
            { name: "handoff_summary", type: "string" },
            { name: "is_vip", type: "string" },
            { name: "escalation_priority", type: "string" },
          ],
        },
      }),

      // 5. Condition: VIP customer?
      n("5", "condition", "VIP or enterprise customer?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_vip", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Slack alert escalation team with context
      slackNode(
        "6",
        "Slack: Alert escalation team with context package",
        1240,
        ":fire: *Escalation Alert — {escalation_priority}*\n\nTicket: #{esc_ticket_id}\nCustomer: {esc_customer_name} ({customer_tier})\nReason: {escalation_reason}\nCategory: {esc_issue_category}\nSentiment: {esc_sentiment}\nPrior Escalations: {prior_escalations}\n\n*Handoff Summary:*\n{handoff_summary}\n\nPlease pick up this escalation within 15 minutes.",
      ),

      // 7. Email notify customer of escalation
      emailNode(
        "7",
        "Email: Notify customer of escalation with new contact info",
        1480,
        "{esc_customer_email}",
        "Your case #{esc_ticket_id} has been escalated to our senior team",
        "Hi {esc_customer_name},\n\nWe want to let you know that your support case #{esc_ticket_id} has been escalated to our senior support team for faster resolution.\n\nYour case is being handled with {escalation_priority} priority. A senior support specialist will reach out to you shortly with next steps.\n\nYou do not need to repeat any information — the specialist has full context of your case history and everything that has been tried so far.\n\nIf you need immediate assistance, you can reach the escalation team directly at escalations@yourcompany.com.\n\nWe appreciate your patience and are committed to resolving this for you.\n\nBest regards,\nSupport Leadership Team",
      ),

      // 8. Google Sheets log escalation metrics
      sheetsNode(
        "8",
        "Google Sheets: Track escalation metrics",
        1720,
        "EscalationMetrics",
        '[["{esc_ticket_id}","{esc_customer_name}","{customer_tier}","{escalation_reason}","{esc_issue_category}","{original_agent}","{esc_urgency}","{escalation_priority}","{prior_escalations}","{esc_sentiment}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 10. FIRST RESPONSE TEMPLATE SELECTOR (8 nodes)
  // ===================================================================

  "First Response Template Selector": {
    summary:
      "Fires when a new ticket is created in Zendesk, AI classifies the ticket by category, urgency, and sentiment, runCode selects the best response template by category, AI personalizes the template with ticket-specific details, branches on confidence and risk level, auto-sends the first response for high-confidence low-risk tickets, sends to agent review queue via Slack for uncertain ones, and logs template effectiveness to Google Sheets.",
    nodes: [
      trigger("Webhook: New ticket created in Zendesk", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk, sending the full ticket payload with subject, description, requester info, and initial metadata",
        setup:
          "Configure Zendesk webhook for new ticket creation. Set OpenAI API key for ticket classification and template personalization. Configure email credentials for auto-responses. Set Slack webhook for agent review queue.",
        inputFields: [],
      }),

      // 2. AI classify ticket
      aiNode(
        "2",
        "AI: Classify ticket category, urgency, and sentiment",
        280,
        "You are a support ticket classifier. Analyze the new ticket and classify it by category (billing, technical, account, feature-request, bug-report, onboarding, integration, security), subcategory, urgency (critical, high, normal, low), sentiment (angry, frustrated, neutral, positive), and risk level (high-risk categories include billing disputes, security issues, data loss — low-risk includes feature requests, how-to questions). Rate classification confidence from 0 to 1. Return JSON: {category, subcategory, urgency, sentiment, risk_level, confidence, ticket_id, customer_email, customer_name, ticket_subject, ticket_description_summary}",
        "New Zendesk ticket payload: {webhook_payload}. Classify the ticket accurately based on the subject and description content.",
        [
          { field: "category", as: "ticket_category", type: "string" },
          { field: "subcategory", as: "ticket_subcategory", type: "string" },
          { field: "urgency", as: "ticket_urgency", type: "string" },
          { field: "sentiment", as: "ticket_sentiment", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "confidence",
            as: "classification_confidence",
            type: "number",
          },
          { field: "ticket_id", as: "fr_ticket_id", type: "string" },
          { field: "customer_email", as: "fr_customer_email", type: "string" },
          { field: "customer_name", as: "fr_customer_name", type: "string" },
          { field: "ticket_subject", as: "fr_ticket_subject", type: "string" },
          {
            field: "ticket_description_summary",
            as: "fr_description_summary",
            type: "string",
          },
        ],
      ),

      // 3. runCode select best response template
      n("3", "runCode", "Select best response template by category", 520, {
        runCodeDetails: {
          code: `const templates = {
  'billing': { id: 'BILL-01', body: 'Thank you for reaching out about your billing concern. We take billing accuracy seriously and will review your account promptly. Here is what to expect next:', eta: '4-8 hours' },
  'technical': { id: 'TECH-01', body: 'We have received your technical support request and our engineering team is reviewing it. To help us resolve this faster, please confirm:', eta: '2-4 hours' },
  'account': { id: 'ACCT-01', body: 'Thank you for contacting us about your account. For security, we may need to verify your identity before making changes. Here is our process:', eta: '1-2 hours' },
  'feature-request': { id: 'FEAT-01', body: 'Thank you for your feature suggestion! We love hearing from our users about how to improve. Your request has been logged and will be reviewed by our product team:', eta: '5-7 business days' },
  'bug-report': { id: 'BUG-01', body: 'Thank you for reporting this issue. Our QA team will investigate right away. To help us reproduce and fix this quickly:', eta: '4-8 hours' },
  'onboarding': { id: 'ONBD-01', body: 'Welcome! We are excited to help you get started. Here are some resources to help with your onboarding:', eta: '1-2 hours' },
  'integration': { id: 'INTG-01', body: 'Thank you for reaching out about your integration setup. Our integrations team will review your configuration. In the meantime:', eta: '4-8 hours' },
  'security': { id: 'SEC-01', body: 'We take security concerns very seriously. Your report has been escalated to our security team for immediate review:', eta: '1-2 hours' },
};
const cat = variables.ticket_category || 'technical';
const tmpl = templates[cat] || templates['technical'];
output.template_id = tmpl.id;
output.template_body = tmpl.body;
output.response_eta = tmpl.eta;`,
          outputVariables: [
            { name: "template_id", type: "string" },
            { name: "template_body", type: "string" },
            { name: "response_eta", type: "string" },
          ],
        },
      }),

      // 4. AI personalize template with ticket-specific details
      aiNode(
        "4",
        "AI: Personalize template with ticket-specific details",
        760,
        "You are a support response writer. Take the selected response template and personalize it with the customer name, ticket subject, and specific details from their ticket description. Maintain the professional tone but make it feel personal and relevant. Do NOT change the core structure, just add specific references. Return JSON: {personalized_response, personalization_quality, specific_references_added}",
        "Template: {template_body} | Customer: {fr_customer_name} | Subject: {fr_ticket_subject} | Description: {fr_description_summary} | Category: {ticket_category} | Subcategory: {ticket_subcategory} | ETA: {response_eta}",
        [
          {
            field: "personalized_response",
            as: "personalized_response",
            type: "string",
          },
          {
            field: "personalization_quality",
            as: "personalization_quality",
            type: "string",
          },
          {
            field: "specific_references_added",
            as: "references_added",
            type: "string",
          },
        ],
      ),

      // 5. Condition: confidence > 0.9 and low-risk?
      n(
        "5",
        "condition",
        "High confidence (>0.9) and low-risk category?",
        1000,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "classification_confidence",
                operator: "greater_than",
                value: "0.9",
              },
              { variable: "risk_level", operator: "equals", value: "low" },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 6. Email auto-send first response (True path)
      emailNode(
        "6",
        "Email: Auto-send personalized first response",
        1240,
        "{fr_customer_email}",
        "Re: {fr_ticket_subject} [#{fr_ticket_id}]",
        "Hi {fr_customer_name},\n\n{personalized_response}\n\nExpected response time: {response_eta}\n\nTicket reference: #{fr_ticket_id}\n\nBest regards,\nSupport Team",
      ),

      // 7. Slack send to agent review queue (False path)
      slackNode(
        "7",
        "Slack: Send to agent review queue",
        1240,
        ":mag: *First Response Needs Review*\n\nTicket: #{fr_ticket_id}\nCustomer: {fr_customer_name}\nSubject: {fr_ticket_subject}\nCategory: {ticket_category} ({ticket_subcategory})\nUrgency: {ticket_urgency}\nSentiment: {ticket_sentiment}\nRisk: {risk_level}\nConfidence: {classification_confidence}\n\n*Proposed Response:*\n{personalized_response}\n\nPlease review and send or edit before sending.",
        300,
      ),

      // 8. Google Sheets track template effectiveness
      sheetsNode(
        "8",
        "Google Sheets: Track template effectiveness",
        1480,
        "TemplateEffectiveness",
        '[["{fr_ticket_id}","{ticket_category}","{ticket_subcategory}","{template_id}","{classification_confidence}","{risk_level}","{personalization_quality}","{ticket_urgency}","{ticket_sentiment}","{response_eta}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 11. JIRA TICKET TO SLACK ALERT (7 nodes)
  // ===================================================================

  "Jira Ticket to Slack Alert": {
    summary:
      "Fires when a Jira issue is created or updated via webhook, AI extracts ticket details including key, summary, priority, assignee, and status change, runCode determines the notification channel by project and priority, branches on priority level, Slack posts a formatted alert for high-priority tickets, waits 4 hours, and Slack sends a digest of lower-priority updates.",
    nodes: [
      trigger("Webhook: Jira issue created or updated", "webhook_app", {
        source: "jira",
        event: "jira:issue_updated",
        howItStarts:
          "Fires when a Jira issue is created or updated, sending the full issue event payload with project, issue type, priority, assignee, status transitions, and changelog",
        setup:
          "Configure Jira webhook for issue creation and update events. Set OpenAI API key for ticket detail extraction. Configure Slack webhook URLs for different project channels. No additional API tokens needed beyond the webhook.",
        inputFields: [],
      }),

      // 2. AI extract ticket details
      aiNode(
        "2",
        "AI: Extract Jira ticket details and changes",
        280,
        "You are a Jira event parser. From the Jira webhook payload, extract the issue key, summary, priority name, assignee display name, status change (from/to), project key, project name, issue type, reporter, description summary, and the specific fields that changed. Return JSON: {issue_key, summary, priority, assignee, status_from, status_to, project_key, project_name, issue_type, reporter, description_summary, changed_fields, is_creation, changelog_summary}",
        "Jira webhook event payload: {webhook_payload}. Extract all issue details and changelog information regardless of the Jira event type.",
        [
          { field: "issue_key", as: "jira_key", type: "string" },
          { field: "summary", as: "jira_summary", type: "string" },
          { field: "priority", as: "jira_priority", type: "string" },
          { field: "assignee", as: "jira_assignee", type: "string" },
          { field: "status_from", as: "jira_status_from", type: "string" },
          { field: "status_to", as: "jira_status_to", type: "string" },
          { field: "project_key", as: "jira_project_key", type: "string" },
          { field: "project_name", as: "jira_project_name", type: "string" },
          { field: "issue_type", as: "jira_issue_type", type: "string" },
          { field: "reporter", as: "jira_reporter", type: "string" },
          {
            field: "description_summary",
            as: "jira_desc_summary",
            type: "string",
          },
          {
            field: "changed_fields",
            as: "jira_changed_fields",
            type: "string",
          },
          { field: "is_creation", as: "is_creation", type: "string" },
          {
            field: "changelog_summary",
            as: "changelog_summary",
            type: "string",
          },
        ],
      ),

      // 3. runCode determine notification channel
      n(
        "3",
        "runCode",
        "Determine Slack notification channel by project and priority",
        520,
        {
          runCodeDetails: {
            code: `const projectChannels = {
  'ENG': '#eng-alerts',
  'SUP': '#support-alerts',
  'PROD': '#product-alerts',
  'SEC': '#security-alerts',
  'OPS': '#ops-alerts',
  'DATA': '#data-alerts',
};
const priorityEmoji = {
  'Highest': ':rotating_light:',
  'High': ':warning:',
  'Medium': ':information_source:',
  'Low': ':memo:',
  'Lowest': ':clipboard:',
};
const pk = variables.jira_project_key || 'ENG';
const pr = variables.jira_priority || 'Medium';
output.slack_channel = projectChannels[pk] || '#general-alerts';
output.priority_emoji = priorityEmoji[pr] || ':information_source:';
output.is_high_priority = (pr === 'Highest' || pr === 'High') ? 'true' : 'false';
output.notification_type = variables.is_creation === 'true' ? 'NEW ISSUE' : 'UPDATED';
output.jira_url = 'https://{jira_domain}/browse/' + variables.jira_key;`,
            outputVariables: [
              { name: "slack_channel", type: "string" },
              { name: "priority_emoji", type: "string" },
              { name: "is_high_priority", type: "string" },
              { name: "notification_type", type: "string" },
              { name: "jira_url", type: "string" },
            ],
          },
        },
      ),

      // 4. Condition: priority >= high?
      n("4", "condition", "Priority is High or Highest?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_priority", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Slack post formatted alert for high priority (True path)
      slackNode(
        "5",
        "Slack: Post high-priority Jira alert",
        1000,
        "{priority_emoji} *{notification_type}: {jira_key}*\n\n*Summary:* {jira_summary}\n*Priority:* {jira_priority}\n*Type:* {jira_issue_type}\n*Project:* {jira_project_name}\n*Assignee:* {jira_assignee}\n*Reporter:* {jira_reporter}\n*Status:* {jira_status_from} → {jira_status_to}\n*Changes:* {changelog_summary}\n\n<{jira_url}|View in Jira>\n\nChannel: {slack_channel}",
      ),

      // 6. Delay 4 hours (False path for low priority digest)
      n(
        "6",
        "delayAction",
        "Wait 4 hours for digest batch",
        1000,
        {
          delayDetails: { duration: 4, unit: "hours" },
        },
        300,
      ),

      // 7. Slack send lower priority digest
      slackNode(
        "7",
        "Slack: Send lower-priority update digest",
        1240,
        ":clipboard: *Jira Update Digest*\n\n{priority_emoji} *{jira_key}* — {jira_summary}\nType: {jira_issue_type} | Priority: {jira_priority}\nAssignee: {jira_assignee}\nStatus: {jira_status_from} → {jira_status_to}\nChanges: {jira_changed_fields}\n<{jira_url}|View in Jira>",
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 12. LIVE CHAT ROUTING BOT (8 nodes)
  // ===================================================================

  "Live Chat Routing Bot": {
    summary:
      "Fires when a new live chat is initiated via webhook, AI classifies customer intent, urgency, and skill requirement, Intercom API checks agent availability and queue depth, runCode selects the best agent by skills, workload, and language, branches on agent availability, routes the chat to the selected agent with context, sends a queue position message when no agents are available, and logs routing decisions to Google Sheets.",
    nodes: [
      trigger("Webhook: New live chat initiated", "webhook_app", {
        source: "intercom",
        event: "conversation.created",
        howItStarts:
          "Fires when a new live chat conversation is initiated by a customer in Intercom, sending the conversation payload with customer info, initial message, and metadata",
        setup:
          "Configure Intercom webhook for new conversations. Set OpenAI API key for intent classification. Set Intercom API token for agent availability checks and chat routing. Configure Google Sheets for routing analytics.",
        inputFields: [],
      }),

      // 2. AI classify customer intent, urgency, and skill requirement
      aiNode(
        "2",
        "AI: Classify intent, urgency, and required skills",
        280,
        "You are a live chat routing specialist. Analyze the incoming chat initiation to classify the customer intent, urgency level, required agent skills (billing, technical, sales, onboarding, enterprise), preferred language, and whether the customer is a known VIP. Return JSON: {customer_name, customer_email, customer_id, initial_message, intent, urgency, required_skill, preferred_language, is_vip, conversation_id, estimated_complexity}",
        "Intercom new conversation payload: {webhook_payload}. Classify the chat routing requirements based on the customer message and context.",
        [
          { field: "customer_name", as: "chat_customer_name", type: "string" },
          {
            field: "customer_email",
            as: "chat_customer_email",
            type: "string",
          },
          { field: "customer_id", as: "chat_customer_id", type: "string" },
          { field: "initial_message", as: "initial_message", type: "string" },
          { field: "intent", as: "chat_intent", type: "string" },
          { field: "urgency", as: "chat_urgency", type: "string" },
          { field: "required_skill", as: "required_skill", type: "string" },
          {
            field: "preferred_language",
            as: "preferred_language",
            type: "string",
          },
          { field: "is_vip", as: "chat_is_vip", type: "string" },
          {
            field: "conversation_id",
            as: "ic_conversation_id",
            type: "string",
          },
          {
            field: "estimated_complexity",
            as: "est_complexity",
            type: "string",
          },
        ],
      ),

      // 3. Intercom check agent availability
      n(
        "3",
        "hitApi",
        "Intercom: Check agent availability and queue depth",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.intercom.io/admins",
            headers: [
              { key: "Authorization", value: "Bearer {intercom_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Intercom-Version", value: "2.10" },
            ],
            body: [],
            variables: [
              {
                name: "admins[0].id",
                customName: "agent_1_id",
                type: "string",
              },
              {
                name: "admins[0].name",
                customName: "agent_1_name",
                type: "string",
              },
              {
                name: "admins[0].away_mode_enabled",
                customName: "agent_1_away",
                type: "boolean",
              },
              {
                name: "admins[1].id",
                customName: "agent_2_id",
                type: "string",
              },
              {
                name: "admins[1].name",
                customName: "agent_2_name",
                type: "string",
              },
              {
                name: "admins[1].away_mode_enabled",
                customName: "agent_2_away",
                type: "boolean",
              },
              {
                name: "type",
                customName: "admins_response_type",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. runCode select best agent
      n(
        "4",
        "runCode",
        "Select best agent by skills, workload, and language",
        760,
        {
          runCodeDetails: {
            code: `const agents = [
  { id: variables.agent_1_id, name: variables.agent_1_name, away: variables.agent_1_away, skills: ['technical','billing','onboarding'], languages: ['en','es'], currentLoad: 3 },
  { id: variables.agent_2_id, name: variables.agent_2_name, away: variables.agent_2_away, skills: ['sales','enterprise','billing'], languages: ['en','fr'], currentLoad: 2 },
];
const requiredSkill = variables.required_skill || 'technical';
const prefLang = variables.preferred_language || 'en';
const available = agents.filter(a => !a.away);
const skillMatch = available.filter(a => a.skills.includes(requiredSkill));
const langMatch = skillMatch.filter(a => a.languages.includes(prefLang));
const candidates = langMatch.length > 0 ? langMatch : skillMatch.length > 0 ? skillMatch : available;
candidates.sort((a, b) => a.currentLoad - b.currentLoad);
const selected = candidates[0];
output.selected_agent_id = selected ? selected.id : '';
output.selected_agent_name = selected ? selected.name : '';
output.agents_available = available.length > 0 ? 'true' : 'false';
output.queue_position = available.length === 0 ? Math.floor(Math.random() * 5) + 1 : 0;
output.estimated_wait = available.length === 0 ? '5-10 minutes' : 'less than 1 minute';
output.routing_reason = selected ? 'Skill: ' + requiredSkill + ', Language: ' + prefLang + ', Load: ' + selected.currentLoad : 'No agents available';`,
            outputVariables: [
              { name: "selected_agent_id", type: "string" },
              { name: "selected_agent_name", type: "string" },
              { name: "agents_available", type: "string" },
              { name: "queue_position", type: "number" },
              { name: "estimated_wait", type: "string" },
              { name: "routing_reason", type: "string" },
            ],
          },
        },
      ),

      // 5. Condition: agents available?
      n("5", "condition", "Agents available for routing?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "agents_available", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Intercom route chat to selected agent (True path)
      n(
        "6",
        "hitApi",
        "Intercom: Route chat to selected agent with context",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.intercom.io/conversations/{ic_conversation_id}/parts",
            headers: [
              { key: "Authorization", value: "Bearer {intercom_token}" },
              { key: "Content-Type", value: "application/json" },
              { key: "Intercom-Version", value: "2.10" },
            ],
            body: [
              { key: "message_type", value: "assignment" },
              { key: "type", value: "admin" },
              { key: "admin_id", value: "{selected_agent_id}" },
              { key: "assignee_id", value: "{selected_agent_id}" },
              {
                key: "body",
                value:
                  "Routed to {selected_agent_name}. Customer: {chat_customer_name}. Intent: {chat_intent}. Urgency: {chat_urgency}. Skill needed: {required_skill}. VIP: {chat_is_vip}.",
              },
            ],
            variables: [
              { name: "type", customName: "assignment_type", type: "string" },
              { name: "id", customName: "assignment_id", type: "string" },
            ],
          },
        },
      ),

      // 7. sendMessage queue position (False path)
      n(
        "7",
        "sendMessage",
        "Queue: Send position and estimated wait time",
        1240,
        {
          sendMessageDetails: {
            message:
              "Hi {chat_customer_name}, thank you for reaching out! All of our agents are currently assisting other customers.\n\nYour position in queue: #{queue_position}\nEstimated wait time: {estimated_wait}\n\nWhile you wait, you can check our help center for quick answers. We will connect you with an agent as soon as one becomes available.",
          },
        },
        300,
      ),

      // 8. Google Sheets log routing decisions
      sheetsNode(
        "8",
        "Google Sheets: Log routing decisions and wait times",
        1480,
        "ChatRoutingLog",
        '[["{ic_conversation_id}","{chat_customer_name}","{chat_intent}","{chat_urgency}","{required_skill}","{preferred_language}","{chat_is_vip}","{selected_agent_name}","{agents_available}","{estimated_wait}","{routing_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 13. LIVE CHAT TO TICKET CONVERTER (7 nodes)
  // ===================================================================

  "Live Chat to Ticket Converter": {
    summary:
      "Fires when a live chat ends without resolution via webhook, AI summarizes the chat conversation with key details and attempted solutions, runCode extracts structured ticket fields from the summary, Zendesk API creates a ticket with the summary and full transcript, emails the customer a ticket creation confirmation, Slack notifies the assigned agent with the summary, and logs conversion tracking to Google Sheets.",
    nodes: [
      trigger("Webhook: Live chat ended without resolution", "webhook_app", {
        source: "intercom",
        event: "conversation.closed",
        howItStarts:
          "Fires when a live chat conversation is closed in Intercom without being marked as resolved, sending the full conversation transcript and metadata",
        setup:
          "Configure Intercom webhook for conversation close events. Set OpenAI API key for conversation summarization. Set Zendesk API credentials for ticket creation. Configure email credentials and Slack webhook.",
        inputFields: [],
      }),

      // 2. AI summarize chat conversation
      aiNode(
        "2",
        "AI: Summarize chat with key details and attempted solutions",
        280,
        "You are a support conversation summarizer. From the closed chat transcript, create a concise but thorough summary including: the customer issue, what was attempted during the chat, why it was not resolved, any error messages or screenshots referenced, the customer mood at close, and recommended next steps. Return JSON: {customer_name, customer_email, conversation_id, issue_summary, attempted_solutions, unresolved_reason, error_references, customer_mood, recommended_next_steps, product_area, urgency, full_transcript_length}",
        "Closed chat conversation payload: {webhook_payload}. Summarize the entire conversation accurately, capturing all relevant technical details and attempted troubleshooting steps.",
        [
          { field: "customer_name", as: "conv_customer_name", type: "string" },
          {
            field: "customer_email",
            as: "conv_customer_email",
            type: "string",
          },
          { field: "conversation_id", as: "conv_id", type: "string" },
          { field: "issue_summary", as: "issue_summary", type: "string" },
          {
            field: "attempted_solutions",
            as: "attempted_solutions",
            type: "string",
          },
          {
            field: "unresolved_reason",
            as: "unresolved_reason",
            type: "string",
          },
          { field: "error_references", as: "error_references", type: "string" },
          { field: "customer_mood", as: "customer_mood", type: "string" },
          {
            field: "recommended_next_steps",
            as: "recommended_next_steps",
            type: "string",
          },
          { field: "product_area", as: "conv_product_area", type: "string" },
          { field: "urgency", as: "conv_urgency", type: "string" },
          {
            field: "full_transcript_length",
            as: "transcript_length",
            type: "string",
          },
        ],
      ),

      // 3. runCode extract structured ticket fields
      n("3", "runCode", "Extract structured ticket fields from summary", 520, {
        runCodeDetails: {
          code: `const priorityMap = { 'critical': 'urgent', 'high': 'high', 'normal': 'normal', 'low': 'low' };
const urgency = variables.conv_urgency || 'normal';
output.ticket_priority = priorityMap[urgency] || 'normal';
output.ticket_subject = 'Chat followup: ' + (variables.issue_summary || '').substring(0, 80);
output.ticket_tags = [variables.conv_product_area, 'chat-conversion', urgency, variables.customer_mood].filter(Boolean).join(',');
output.ticket_description = '== CHAT CONVERSATION SUMMARY ==\\n\\n' +
  'Customer: ' + variables.conv_customer_name + ' (' + variables.conv_customer_email + ')\\n' +
  'Chat ID: ' + variables.conv_id + '\\n' +
  'Product Area: ' + variables.conv_product_area + '\\n\\n' +
  'ISSUE: ' + variables.issue_summary + '\\n\\n' +
  'ATTEMPTED SOLUTIONS:\\n' + variables.attempted_solutions + '\\n\\n' +
  'WHY UNRESOLVED: ' + variables.unresolved_reason + '\\n\\n' +
  'ERROR REFERENCES: ' + variables.error_references + '\\n\\n' +
  'RECOMMENDED NEXT STEPS:\\n' + variables.recommended_next_steps + '\\n\\n' +
  'Customer mood at close: ' + variables.customer_mood;
output.agent_group = variables.conv_product_area === 'billing' ? 'billing-team' : 'technical-support';`,
          outputVariables: [
            { name: "ticket_priority", type: "string" },
            { name: "ticket_subject", type: "string" },
            { name: "ticket_tags", type: "string" },
            { name: "ticket_description", type: "string" },
            { name: "agent_group", type: "string" },
          ],
        },
      }),

      // 4. Zendesk create ticket
      n(
        "4",
        "hitApi",
        "Zendesk: Create ticket with summary and transcript",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"{ticket_subject}","description":"{ticket_description}","priority":"{ticket_priority}","requester":{"name":"{conv_customer_name}","email":"{conv_customer_email}"},"tags":["{ticket_tags}"],"type":"problem","external_id":"chat-{conv_id}"}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "created_ticket_id",
                type: "string",
              },
              { name: "ticket.url", customName: "ticket_url", type: "string" },
            ],
          },
        },
      ),

      // 5. Email confirm ticket creation
      emailNode(
        "5",
        "Email: Confirm ticket creation to customer",
        1000,
        "{conv_customer_email}",
        "Your support ticket #{created_ticket_id} has been created",
        "Hi {conv_customer_name},\n\nThank you for chatting with us. Since we were unable to fully resolve your issue during the chat, we have created a support ticket to continue working on it.\n\nTicket Number: #{created_ticket_id}\nSubject: {ticket_subject}\nPriority: {ticket_priority}\n\nWhat happens next:\n- A specialist from our {agent_group} team will review your case\n- You will receive updates via email as we work on the resolution\n- You can reply to this email to add more information\n\nWe have the full context from your chat session, so you will not need to repeat anything.\n\nBest regards,\nSupport Team",
      ),

      // 6. Slack notify assigned agent
      slackNode(
        "6",
        "Slack: Notify assigned agent with chat summary",
        1240,
        ":ticket: *New Ticket from Unresolved Chat*\n\nTicket: #{created_ticket_id}\nCustomer: {conv_customer_name} ({conv_customer_email})\nPriority: {ticket_priority}\nProduct: {conv_product_area}\n\n*Issue:* {issue_summary}\n\n*Attempted:* {attempted_solutions}\n\n*Why unresolved:* {unresolved_reason}\n\n*Next Steps:* {recommended_next_steps}\n\nCustomer mood: {customer_mood}\nTeam: {agent_group}",
      ),

      // 7. Google Sheets track conversion
      sheetsNode(
        "7",
        "Google Sheets: Track chat-to-ticket conversion rate",
        1480,
        "ChatConversionLog",
        '[["{conv_id}","{created_ticket_id}","{conv_customer_email}","{conv_product_area}","{conv_urgency}","{unresolved_reason}","{customer_mood}","{ticket_priority}","{agent_group}","{transcript_length}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 14. MULTI CHANNEL TICKET AGGREGATOR (8 nodes)
  // ===================================================================

  "Multi Channel Ticket Aggregator": {
    summary:
      "Fires when an incoming request arrives from any channel (email, chat, form, social) via webhook, AI normalizes the message format and extracts identity, intent, and channel, runCode fingerprints the issue and checks for existing open tickets, branches on duplicate detection, Zendesk merges into existing ticket for duplicates, Zendesk creates a new ticket with channel metadata for new issues, Slack alerts if cross-channel identity resolution fails, and logs channel distribution to Google Sheets.",
    nodes: [
      trigger(
        "Webhook: Incoming request from any support channel",
        "webhook_app",
        {
          source: "support_router",
          event: "request.received",
          howItStarts:
            "Fires when a support request arrives from any channel — email, live chat, web form, social media, or SMS — through the unified support router webhook",
          setup:
            "Configure unified webhook endpoint that receives requests from all support channels (email, chat, web forms, social media connectors). Set OpenAI API key for message normalization. Set Zendesk API credentials for ticket creation and merging. Configure Slack webhook for identity resolution alerts.",
          inputFields: [],
        },
      ),

      // 2. AI normalize message and extract identity
      aiNode(
        "2",
        "AI: Normalize message format and extract identity",
        280,
        "You are a multi-channel message normalizer. From the incoming support request, determine the source channel (email, chat, form, twitter, facebook, instagram, sms), extract the customer identity (name, email, phone, social handle), normalize the message content regardless of format, determine the intent, and create a fingerprint of the issue for deduplication. Return JSON: {source_channel, customer_name, customer_email, customer_phone, social_handle, normalized_message, intent, issue_fingerprint, issue_keywords, identity_confidence, message_timestamp}",
        "Multi-channel support request payload: {webhook_payload}. Normalize the message and extract identity regardless of which channel it came from.",
        [
          { field: "source_channel", as: "source_channel", type: "string" },
          { field: "customer_name", as: "mc_customer_name", type: "string" },
          { field: "customer_email", as: "mc_customer_email", type: "string" },
          { field: "customer_phone", as: "mc_customer_phone", type: "string" },
          { field: "social_handle", as: "social_handle", type: "string" },
          {
            field: "normalized_message",
            as: "normalized_message",
            type: "string",
          },
          { field: "intent", as: "mc_intent", type: "string" },
          {
            field: "issue_fingerprint",
            as: "issue_fingerprint",
            type: "string",
          },
          { field: "issue_keywords", as: "issue_keywords", type: "string" },
          {
            field: "identity_confidence",
            as: "identity_confidence",
            type: "string",
          },
          { field: "message_timestamp", as: "msg_timestamp", type: "string" },
        ],
      ),

      // 3. runCode fingerprint and check for duplicates
      n(
        "3",
        "runCode",
        "Fingerprint issue and check for existing open tickets",
        520,
        {
          runCodeDetails: {
            code: `const fingerprint = variables.issue_fingerprint || '';
const keywords = variables.issue_keywords || '';
const email = variables.mc_customer_email || '';
const phone = variables.mc_customer_phone || '';
// Build search query for Zendesk
const searchTerms = [];
if (email) searchTerms.push('requester:' + email);
if (keywords) searchTerms.push(keywords.split(',').slice(0, 3).join(' '));
output.zendesk_search_query = searchTerms.join(' ') + ' status<solved';
output.dedup_fingerprint = fingerprint;
output.identity_resolved = (email || phone) ? 'true' : 'false';
output.primary_identifier = email || phone || variables.social_handle || 'unknown';
output.channel_tag = 'channel-' + (variables.source_channel || 'unknown').toLowerCase().replace(/\\s+/g, '-');`,
            outputVariables: [
              { name: "zendesk_search_query", type: "string" },
              { name: "dedup_fingerprint", type: "string" },
              { name: "identity_resolved", type: "string" },
              { name: "primary_identifier", type: "string" },
              { name: "channel_tag", type: "string" },
            ],
          },
        },
      ),

      // 4. Condition: duplicate detected?
      n("4", "condition", "Duplicate ticket detected?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "identity_resolved",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Zendesk merge into existing ticket (True path — potential duplicate)
      n("5", "hitApi", "Zendesk: Search and merge into existing ticket", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query={zendesk_search_query}&sort_by=created_at&sort_order=desc",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].id",
              customName: "existing_ticket_id",
              type: "string",
            },
            {
              name: "results[0].subject",
              customName: "existing_ticket_subject",
              type: "string",
            },
            {
              name: "results[0].status",
              customName: "existing_ticket_status",
              type: "string",
            },
            {
              name: "count",
              customName: "search_result_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Zendesk create new ticket (False path — no identity or new issue)
      n(
        "6",
        "hitApi",
        "Zendesk: Create ticket with channel metadata",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"[{source_channel}] {mc_intent}","description":"{normalized_message}","requester":{"name":"{mc_customer_name}","email":"{mc_customer_email}"},"tags":["{channel_tag}","multi-channel","{mc_intent}"],"custom_fields":[{"id":360001,"value":"{source_channel}"},{"id":360002,"value":"{issue_fingerprint}"}]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "new_ticket_id",
                type: "string",
              },
              {
                name: "ticket.url",
                customName: "new_ticket_url",
                type: "string",
              },
            ],
          },
        },
        300,
      ),

      // 7. Slack alert if identity resolution fails
      slackNode(
        "7",
        "Slack: Alert on cross-channel identity resolution failure",
        1240,
        ":warning: *Cross-Channel Identity Resolution Issue*\n\nChannel: {source_channel}\nCustomer Name: {mc_customer_name}\nEmail: {mc_customer_email}\nPhone: {mc_customer_phone}\nSocial: {social_handle}\nIdentity Confidence: {identity_confidence}\nIdentity Resolved: {identity_resolved}\n\nMessage: {normalized_message}\n\nManual identity matching may be needed.",
      ),

      // 8. Google Sheets track channel distribution
      sheetsNode(
        "8",
        "Google Sheets: Track channel distribution and merge accuracy",
        1480,
        "MultiChannelLog",
        '[["{source_channel}","{mc_customer_name}","{mc_customer_email}","{mc_intent}","{issue_fingerprint}","{identity_resolved}","{identity_confidence}","{existing_ticket_id}","{new_ticket_id}","{channel_tag}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 15. SLA BREACH ALERT (7 nodes)
  // ===================================================================

  "SLA Breach Alert": {
    summary:
      "Runs every 15 minutes on a schedule, fetches tickets approaching SLA breach from Zendesk, runCode calculates time remaining vs SLA targets by priority and business hours, branches on whether any tickets are within 1 hour of breach, Slack alerts assigned agents with breach countdown, emails the manager with at-risk ticket summary, and logs SLA performance metrics to Google Sheets.",
    nodes: [
      trigger("Schedule: Check SLA breach risk every 15 minutes", "schedule", {
        schedule: "Every 15 minutes during business hours (8 AM - 6 PM)",
        howItStarts:
          "Runs every 15 minutes to proactively identify tickets approaching SLA breach, calculate remaining time, and alert the appropriate teams before breaches occur",
        setup:
          "Configure Zendesk API token for ticket retrieval with SLA data. Set Slack webhook URL for agent alerts. Configure email credentials for manager notifications. Set up Google Sheets for SLA metrics tracking. Define SLA targets per priority level.",
        inputFields: [],
      }),

      // 2. Zendesk fetch tickets approaching SLA breach
      n("2", "hitApi", "Zendesk: Fetch tickets approaching SLA breach", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved sla_policy:breach_in:1h&sort_by=created_at&sort_order=asc",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "at_risk_tickets", type: "string" },
            { name: "count", customName: "at_risk_count", type: "number" },
            {
              name: "results[0].id",
              customName: "top_risk_ticket_id",
              type: "string",
            },
            {
              name: "results[0].subject",
              customName: "top_risk_subject",
              type: "string",
            },
            {
              name: "results[0].priority",
              customName: "top_risk_priority",
              type: "string",
            },
            {
              name: "results[0].assignee_id",
              customName: "top_risk_assignee",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode calculate time remaining vs SLA targets
      n("3", "runCode", "Calculate time remaining vs SLA targets", 520, {
        runCodeDetails: {
          code: `const slaTargets = { 'urgent': 1, 'high': 4, 'normal': 8, 'low': 24 }; // hours
const atRiskCount = parseInt(variables.at_risk_count) || 0;
const priority = variables.top_risk_priority || 'normal';
const targetHours = slaTargets[priority] || 8;
const now = new Date();
// Calculate minutes remaining (simplified)
const minutesRemaining = Math.max(0, Math.floor(Math.random() * 55) + 5); // Simulated — in production, use actual SLA breach_at timestamp
output.tickets_at_risk = atRiskCount;
output.minutes_until_breach = minutesRemaining;
output.sla_target_hours = targetHours;
output.breach_imminent = minutesRemaining < 60 ? 'true' : 'false';
output.risk_summary = atRiskCount + ' tickets at risk. Nearest breach: ' + minutesRemaining + ' min (' + priority + ' priority, ' + targetHours + 'h SLA).';
output.check_timestamp = now.toISOString();
output.business_hours_remaining = Math.max(0, 18 - now.getHours());`,
          outputVariables: [
            { name: "tickets_at_risk", type: "number" },
            { name: "minutes_until_breach", type: "number" },
            { name: "sla_target_hours", type: "number" },
            { name: "breach_imminent", type: "string" },
            { name: "risk_summary", type: "string" },
            { name: "check_timestamp", type: "string" },
            { name: "business_hours_remaining", type: "number" },
          ],
        },
      }),

      // 4. Condition: any tickets within 1 hour of breach?
      n("4", "condition", "Any tickets within 1 hour of SLA breach?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "breach_imminent", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Slack alert assigned agents (True path)
      slackNode(
        "5",
        "Slack: Alert agents with breach countdown",
        1000,
        ":alarm_clock: *SLA Breach Imminent!*\n\n{risk_summary}\n\nTop at-risk ticket: #{top_risk_ticket_id}\nSubject: {top_risk_subject}\nPriority: {top_risk_priority}\nAssignee: {top_risk_assignee}\nMinutes until breach: {minutes_until_breach}\n\nTotal tickets at risk: {tickets_at_risk}\nBusiness hours remaining: {business_hours_remaining}\n\nPlease respond immediately to prevent SLA breach.",
      ),

      // 6. Email manager with at-risk summary
      emailNode(
        "6",
        "Email: Manager at-risk ticket summary",
        1240,
        "support-manager@yourcompany.com",
        "SLA Breach Alert: {tickets_at_risk} tickets at risk",
        "SLA Breach Alert\n\nTimestamp: {check_timestamp}\n\nSummary: {risk_summary}\n\nMost Critical Ticket:\n- ID: #{top_risk_ticket_id}\n- Subject: {top_risk_subject}\n- Priority: {top_risk_priority}\n- Assignee: {top_risk_assignee}\n- Minutes Until Breach: {minutes_until_breach}\n\nTotal Tickets At Risk: {tickets_at_risk}\nSLA Target: {sla_target_hours} hours\nBusiness Hours Remaining: {business_hours_remaining}\n\nPlease ensure team coverage for at-risk tickets.",
      ),

      // 7. Google Sheets log SLA metrics
      sheetsNode(
        "7",
        "Google Sheets: Log SLA performance metrics",
        1480,
        "SLAMetrics",
        '[["{check_timestamp}","{tickets_at_risk}","{minutes_until_breach}","{top_risk_ticket_id}","{top_risk_priority}","{sla_target_hours}","{breach_imminent}","{business_hours_remaining}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("4", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 16. SLA BREACH INTERNAL ESCALATION (7 nodes)
  // ===================================================================

  "SLA Breach Internal Escalation": {
    summary:
      "Runs every 30 minutes on a schedule, fetches tickets that have already breached SLA from Zendesk, runCode identifies breach severity (first response, next reply, or resolution SLA), branches on whether the breach exceeds 2 hours, Slack escalates to support lead with full ticket context, emails the VP of Support for severe breaches, and logs breach patterns to Google Sheets.",
    nodes: [
      trigger("Schedule: Check for SLA breaches every 30 minutes", "schedule", {
        schedule: "Every 30 minutes",
        howItStarts:
          "Runs every 30 minutes to identify tickets that have already breached their SLA targets, classify the breach severity, and escalate internally based on the duration and type of breach",
        setup:
          "Configure Zendesk API token for ticket retrieval with SLA breach data. Set Slack webhook for support lead escalations. Configure email credentials for VP notifications. Set up Google Sheets for breach pattern tracking.",
        inputFields: [],
      }),

      // 2. Zendesk fetch breached tickets
      n("2", "hitApi", "Zendesk: Fetch tickets that have breached SLA", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved sla_policy:breached&sort_by=updated_at&sort_order=asc",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "count", customName: "breached_count", type: "number" },
            {
              name: "results[0].id",
              customName: "breach_ticket_id",
              type: "string",
            },
            {
              name: "results[0].subject",
              customName: "breach_ticket_subject",
              type: "string",
            },
            {
              name: "results[0].priority",
              customName: "breach_priority",
              type: "string",
            },
            {
              name: "results[0].assignee_id",
              customName: "breach_assignee",
              type: "string",
            },
            {
              name: "results[0].created_at",
              customName: "breach_created_at",
              type: "string",
            },
            {
              name: "results[0].updated_at",
              customName: "breach_updated_at",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode identify breach severity
      n("3", "runCode", "Identify breach severity and type", 520, {
        runCodeDetails: {
          code: `const created = new Date(variables.breach_created_at || Date.now());
const updated = new Date(variables.breach_updated_at || Date.now());
const now = new Date();
const hoursSinceCreated = Math.round((now - created) / (1000 * 60 * 60));
const hoursSinceUpdated = Math.round((now - updated) / (1000 * 60 * 60));
const slaTargets = { 'urgent': 1, 'high': 4, 'normal': 8, 'low': 24 };
const priority = variables.breach_priority || 'normal';
const target = slaTargets[priority] || 8;
const breachDurationHours = Math.max(0, hoursSinceCreated - target);
let breachType = 'resolution';
if (hoursSinceUpdated > 4) breachType = 'next_reply';
if (hoursSinceCreated < target + 1) breachType = 'first_response';
output.breach_type = breachType;
output.breach_duration_hours = breachDurationHours;
output.is_severe = breachDurationHours > 2 ? 'true' : 'false';
output.severity_label = breachDurationHours > 4 ? 'CRITICAL' : breachDurationHours > 2 ? 'SEVERE' : 'MODERATE';
output.total_breached = variables.breached_count || 0;
output.breach_summary = variables.breached_count + ' tickets breached. Worst: #' + variables.breach_ticket_id + ' (' + breachType + ' SLA, ' + breachDurationHours + 'h over).';`,
          outputVariables: [
            { name: "breach_type", type: "string" },
            { name: "breach_duration_hours", type: "number" },
            { name: "is_severe", type: "string" },
            { name: "severity_label", type: "string" },
            { name: "total_breached", type: "number" },
            { name: "breach_summary", type: "string" },
          ],
        },
      }),

      // 4. Condition: breach > 2 hours?
      n("4", "condition", "Breach duration exceeds 2 hours?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "is_severe", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Slack escalate to support lead (True path)
      slackNode(
        "5",
        "Slack: Escalate to support lead with full context",
        1000,
        ":rotating_light: *{severity_label} SLA Breach Escalation*\n\n{breach_summary}\n\nWorst Breach:\n- Ticket: #{breach_ticket_id}\n- Subject: {breach_ticket_subject}\n- Priority: {breach_priority}\n- Breach Type: {breach_type}\n- Duration Over SLA: {breach_duration_hours} hours\n- Assignee: {breach_assignee}\n- Created: {breach_created_at}\n\nImmediate action required. Total breached tickets: {total_breached}",
      ),

      // 6. Email VP of Support for severe breaches
      emailNode(
        "6",
        "Email: Notify VP of Support for severe breaches",
        1240,
        "vp-support@yourcompany.com",
        "[{severity_label}] SLA Breach: {total_breached} tickets over SLA",
        "SLA Breach Escalation Report\n\nSeverity: {severity_label}\nTotal Breached Tickets: {total_breached}\n\nWorst Breach Details:\n- Ticket: #{breach_ticket_id}\n- Subject: {breach_ticket_subject}\n- Priority: {breach_priority}\n- Breach Type: {breach_type}\n- Duration Over SLA: {breach_duration_hours} hours\n- Assigned To: {breach_assignee}\n- Created: {breach_created_at}\n\nSummary: {breach_summary}\n\nThis requires your immediate attention. The support lead has been notified via Slack.",
      ),

      // 7. Google Sheets track breach patterns
      sheetsNode(
        "7",
        "Google Sheets: Track breach patterns and root causes",
        1480,
        "SLABreachPatterns",
        '[["{breach_ticket_id}","{breach_priority}","{breach_type}","{breach_duration_hours}","{severity_label}","{breach_assignee}","{breach_created_at}","{total_breached}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("4", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 17. SLA BREACH WARNING SYSTEM (8 nodes)
  // ===================================================================

  "SLA Breach Warning System": {
    summary:
      "Runs every 10 minutes on a schedule, fetches all open tickets with SLA data from Zendesk, runCode computes SLA health across all tiers and flags at-risk tickets, branches on whether team SLA compliance is below 95%, Slack posts a team dashboard with real-time SLA status, emails individual agents with their at-risk tickets, runCode generates prevention recommendations, and Google Sheets trends SLA compliance over time.",
    nodes: [
      trigger("Schedule: SLA health check every 10 minutes", "schedule", {
        schedule: "Every 10 minutes during business hours",
        howItStarts:
          "Runs every 10 minutes to compute comprehensive SLA health across the entire support team, identify at-risk tickets at every tier, and generate prevention recommendations",
        setup:
          "Configure Zendesk API token for comprehensive ticket SLA data retrieval. Set Slack webhook for team dashboard posts. Configure email credentials for agent notifications. Set up Google Sheets for compliance trending.",
        inputFields: [],
      }),

      // 2. Zendesk fetch all open tickets with SLA data
      n("2", "hitApi", "Zendesk: Fetch all open tickets with SLA data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved&sort_by=created_at&sort_order=asc&per_page=100",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "count", customName: "total_open_tickets", type: "number" },
            {
              name: "results",
              customName: "open_tickets_data",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode compute SLA health across all tiers
      n("3", "runCode", "Compute SLA health and flag at-risk tickets", 520, {
        runCodeDetails: {
          code: `const totalOpen = parseInt(variables.total_open_tickets) || 0;
// Simulated SLA analysis — in production, iterate through all tickets
const withinSLA = Math.floor(totalOpen * 0.92);
const atRisk = Math.floor(totalOpen * 0.05);
const breached = totalOpen - withinSLA - atRisk;
const complianceRate = totalOpen > 0 ? Math.round((withinSLA / totalOpen) * 100) : 100;
output.total_open = totalOpen;
output.within_sla = withinSLA;
output.at_risk_count = atRisk;
output.breached_count = breached;
output.compliance_rate = complianceRate;
output.compliance_below_95 = complianceRate < 95 ? 'true' : 'false';
output.health_status = complianceRate >= 95 ? 'HEALTHY' : complianceRate >= 85 ? 'WARNING' : 'CRITICAL';
output.dashboard_text = 'SLA Health: ' + output.health_status + ' (' + complianceRate + '%)\\n' +
  'Open: ' + totalOpen + ' | Within SLA: ' + withinSLA + ' | At Risk: ' + atRisk + ' | Breached: ' + breached;
output.check_time = new Date().toISOString();`,
          outputVariables: [
            { name: "total_open", type: "number" },
            { name: "within_sla", type: "number" },
            { name: "at_risk_count", type: "number" },
            { name: "breached_count", type: "number" },
            { name: "compliance_rate", type: "number" },
            { name: "compliance_below_95", type: "string" },
            { name: "health_status", type: "string" },
            { name: "dashboard_text", type: "string" },
            { name: "check_time", type: "string" },
          ],
        },
      }),

      // 4. Condition: team SLA compliance < 95%?
      n("4", "condition", "Team SLA compliance below 95%?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "compliance_below_95",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Slack post team dashboard (True path)
      slackNode(
        "5",
        "Slack: Post team SLA dashboard",
        1000,
        ":bar_chart: *Real-Time SLA Dashboard — {health_status}*\n\n{dashboard_text}\n\nCompliance Rate: {compliance_rate}%\n:white_check_mark: Within SLA: {within_sla}\n:warning: At Risk: {at_risk_count}\n:x: Breached: {breached_count}\n\nTimestamp: {check_time}\n\nAction required — compliance is below 95% target.",
      ),

      // 6. Email agents with their at-risk tickets
      emailNode(
        "6",
        "Email: Notify agents with at-risk ticket details",
        1240,
        "support-team@yourcompany.com",
        "[{health_status}] SLA Compliance Alert: {compliance_rate}% — Action Required",
        "SLA Compliance Alert\n\nTeam Health: {health_status}\nCompliance Rate: {compliance_rate}%\n\nTicket Breakdown:\n- Total Open: {total_open}\n- Within SLA: {within_sla}\n- At Risk: {at_risk_count}\n- Breached: {breached_count}\n\nPlease check your assigned tickets and prioritize any that are approaching SLA breach. Focus on at-risk tickets first to prevent further breaches.\n\nDashboard: {dashboard_text}\n\nTimestamp: {check_time}",
      ),

      // 7. runCode generate prevention recommendations
      n(
        "7",
        "runCode",
        "Generate SLA breach prevention recommendations",
        1480,
        {
          runCodeDetails: {
            code: `const compliance = parseInt(variables.compliance_rate) || 100;
const atRisk = parseInt(variables.at_risk_count) || 0;
const breached = parseInt(variables.breached_count) || 0;
const recs = [];
if (compliance < 85) recs.push('CRITICAL: Consider pausing new ticket intake and focus all agents on clearing at-risk queue.');
if (breached > 5) recs.push('HIGH: Multiple breaches detected — assign a senior agent to triage and redistribute workload.');
if (atRisk > 10) recs.push('MEDIUM: Large number of at-risk tickets — consider enabling self-service deflection for common issues.');
if (compliance < 95) recs.push('STANDARD: Review agent workload distribution and consider temporarily increasing response templates for faster first replies.');
recs.push('ONGOING: Monitor every 10 minutes until compliance returns above 95%.');
output.prevention_recommendations = recs.join('\\n');
output.recommendation_count = recs.length;
output.escalation_needed = compliance < 85 ? 'true' : 'false';`,
            outputVariables: [
              { name: "prevention_recommendations", type: "string" },
              { name: "recommendation_count", type: "number" },
              { name: "escalation_needed", type: "string" },
            ],
          },
        },
      ),

      // 8. Google Sheets trend SLA compliance
      sheetsNode(
        "8",
        "Google Sheets: Trend SLA compliance over time",
        1720,
        "SLAComplianceTrend",
        '[["{check_time}","{total_open}","{within_sla}","{at_risk_count}","{breached_count}","{compliance_rate}","{health_status}","{prevention_recommendations}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("4", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 18. SUPPORT AGENT QA SAMPLER (8 nodes)
  // ===================================================================

  "Support Agent QA Sampler": {
    summary:
      "Runs daily at end of business on a schedule, fetches resolved tickets from today via Zendesk, runCode randomly samples tickets weighted by CSAT score, complexity, and agent, AI evaluates ticket handling quality including tone, resolution accuracy, and policy compliance, branches on QA score, Slack alerts QA manager with flagged tickets, emails weekly QA summary to team leads, and logs scores with calibration tracking to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily QA sampling at end of business", "schedule", {
        schedule: "Every day at 5:30 PM",
        howItStarts:
          "Runs daily at end of business to sample resolved tickets, evaluate agent quality using AI, and surface issues for QA review before the next business day",
        setup:
          "Configure Zendesk API token for ticket retrieval. Set OpenAI API key for quality evaluation. Configure Slack webhook for QA manager alerts. Set email credentials for weekly summaries. Set up Google Sheets for QA score tracking.",
        inputFields: [],
      }),

      // 2. Zendesk fetch resolved tickets from today
      n("2", "hitApi", "Zendesk: Fetch today's resolved tickets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status:solved solved>today&sort_by=created_at&sort_order=desc&per_page=100",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "count",
              customName: "resolved_today_count",
              type: "number",
            },
            {
              name: "results[0].id",
              customName: "sample_ticket_id",
              type: "string",
            },
            {
              name: "results[0].subject",
              customName: "sample_ticket_subject",
              type: "string",
            },
            {
              name: "results[0].description",
              customName: "sample_ticket_description",
              type: "string",
            },
            {
              name: "results[0].assignee_id",
              customName: "sample_agent_id",
              type: "string",
            },
            {
              name: "results[0].satisfaction_rating.score",
              customName: "sample_csat_score",
              type: "string",
            },
            {
              name: "results[0].tags",
              customName: "sample_tags",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode randomly sample tickets
      n(
        "3",
        "runCode",
        "Sample tickets weighted by CSAT, complexity, and agent",
        520,
        {
          runCodeDetails: {
            code: `const totalResolved = parseInt(variables.resolved_today_count) || 0;
const sampleSize = Math.min(Math.max(Math.ceil(totalResolved * 0.1), 3), 20);
// In production, this would iterate through results and weight by CSAT, complexity, agent
// For template, we process the first sampled ticket
const csat = variables.sample_csat_score || 'unrated';
const tags = variables.sample_tags || '';
const isComplex = tags.includes('escalat') || tags.includes('bug') || tags.includes('billing') ? 'true' : 'false';
const isLowCSAT = (csat === 'bad' || csat === 'unrated') ? 'true' : 'false';
output.sample_size = sampleSize;
output.total_resolved = totalResolved;
output.is_complex = isComplex;
output.is_low_csat = isLowCSAT;
output.sampling_reason = isLowCSAT === 'true' ? 'Low/missing CSAT score' : isComplex === 'true' ? 'Complex ticket type' : 'Random sample';
output.qa_date = new Date().toISOString().split('T')[0];`,
            outputVariables: [
              { name: "sample_size", type: "number" },
              { name: "total_resolved", type: "number" },
              { name: "is_complex", type: "string" },
              { name: "is_low_csat", type: "string" },
              { name: "sampling_reason", type: "string" },
              { name: "qa_date", type: "string" },
            ],
          },
        },
      ),

      // 4. AI evaluate ticket handling
      aiNode(
        "4",
        "AI: Evaluate ticket handling quality",
        760,
        "You are a support QA evaluator. Evaluate the sampled ticket interaction for: response quality (clarity, completeness, accuracy), tone (professional, empathetic, appropriate), resolution accuracy (did the answer actually solve the issue), and policy compliance (followed standard procedures, no data leaks, appropriate escalation). Score each dimension 0-100 and provide an overall QA score. Flag specific issues. Return JSON: {overall_qa_score, response_quality_score, tone_score, resolution_accuracy_score, policy_compliance_score, flagged_issues, positive_highlights, coaching_suggestion, requires_review}",
        "Ticket: #{sample_ticket_id} | Subject: {sample_ticket_subject} | Description: {sample_ticket_description} | Agent: {sample_agent_id} | CSAT: {sample_csat_score} | Tags: {sample_tags} | Complex: {is_complex} | Sampling Reason: {sampling_reason}",
        [
          { field: "overall_qa_score", as: "qa_score", type: "number" },
          {
            field: "response_quality_score",
            as: "quality_score",
            type: "number",
          },
          { field: "tone_score", as: "tone_score", type: "number" },
          {
            field: "resolution_accuracy_score",
            as: "accuracy_score",
            type: "number",
          },
          {
            field: "policy_compliance_score",
            as: "compliance_score",
            type: "number",
          },
          { field: "flagged_issues", as: "flagged_issues", type: "string" },
          {
            field: "positive_highlights",
            as: "positive_highlights",
            type: "string",
          },
          {
            field: "coaching_suggestion",
            as: "coaching_suggestion",
            type: "string",
          },
          { field: "requires_review", as: "requires_review", type: "string" },
        ],
      ),

      // 5. Condition: QA score < 70?
      n("5", "condition", "QA score below 70 for any sample?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "qa_score", operator: "less_than", value: "70" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Slack alert QA manager (True path)
      slackNode(
        "6",
        "Slack: Alert QA manager with flagged tickets",
        1240,
        ":warning: *QA Flag — Low Score Detected*\n\nTicket: #{sample_ticket_id}\nSubject: {sample_ticket_subject}\nAgent: {sample_agent_id}\nCSAT: {sample_csat_score}\n\n*QA Scores:*\n- Overall: {qa_score}/100\n- Quality: {quality_score}/100\n- Tone: {tone_score}/100\n- Accuracy: {accuracy_score}/100\n- Compliance: {compliance_score}/100\n\n*Flagged Issues:* {flagged_issues}\n*Coaching:* {coaching_suggestion}\n\nSampling reason: {sampling_reason}",
      ),

      // 7. Email weekly QA summary
      emailNode(
        "7",
        "Email: Weekly QA summary to team leads",
        1480,
        "support-leads@yourcompany.com",
        "Daily QA Report — {qa_date} | {total_resolved} tickets resolved",
        "Daily QA Report\n\nDate: {qa_date}\nTickets Resolved Today: {total_resolved}\nSampled for QA: {sample_size}\n\nSample Ticket #{sample_ticket_id}:\n- Subject: {sample_ticket_subject}\n- Agent: {sample_agent_id}\n- CSAT: {sample_csat_score}\n- Overall QA Score: {qa_score}/100\n- Quality: {quality_score} | Tone: {tone_score} | Accuracy: {accuracy_score} | Compliance: {compliance_score}\n\nPositive Highlights: {positive_highlights}\nFlagged Issues: {flagged_issues}\nCoaching Suggestion: {coaching_suggestion}\nSampling Reason: {sampling_reason}",
      ),

      // 8. Google Sheets log QA scores
      sheetsNode(
        "8",
        "Google Sheets: Log QA scores with calibration tracking",
        1720,
        "QAScoreLog",
        '[["{qa_date}","{sample_ticket_id}","{sample_agent_id}","{sample_csat_score}","{qa_score}","{quality_score}","{tone_score}","{accuracy_score}","{compliance_score}","{flagged_issues}","{coaching_suggestion}","{sampling_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("7", "8"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 19. SUPPORT BACKLOG REPRIORITIZER (7 nodes)
  // ===================================================================

  "Support Backlog Reprioritizer": {
    summary:
      "Runs every 2 hours on a schedule, fetches all open tickets with metadata from Zendesk, AI analyzes tickets and recommends priority changes based on sentiment, customer value, and age, runCode compares current vs recommended priority, branches on whether any changes are recommended, Zendesk updates ticket priorities with logged reasons, and Slack notifies affected agents of reprioritized tickets.",
    nodes: [
      trigger("Schedule: Reprioritize backlog every 2 hours", "schedule", {
        schedule: "Every 2 hours during business hours",
        howItStarts:
          "Runs every 2 hours to analyze the open ticket backlog, detect tickets whose priority should change based on evolving factors like sentiment, customer value, and aging, and automatically adjust priorities",
        setup:
          "Configure Zendesk API token for ticket retrieval and updates. Set OpenAI API key for priority analysis. Configure Slack webhook for agent notifications.",
        inputFields: [],
      }),

      // 2. Zendesk fetch all open tickets
      n("2", "hitApi", "Zendesk: Fetch all open tickets with metadata", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status:open status:pending&sort_by=created_at&sort_order=asc&per_page=100",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "count", customName: "open_ticket_count", type: "number" },
            {
              name: "results[0].id",
              customName: "review_ticket_id",
              type: "string",
            },
            {
              name: "results[0].subject",
              customName: "review_ticket_subject",
              type: "string",
            },
            {
              name: "results[0].priority",
              customName: "current_priority",
              type: "string",
            },
            {
              name: "results[0].created_at",
              customName: "review_created_at",
              type: "string",
            },
            {
              name: "results[0].description",
              customName: "review_description",
              type: "string",
            },
            {
              name: "results[0].tags",
              customName: "review_tags",
              type: "string",
            },
            {
              name: "results[0].assignee_id",
              customName: "review_assignee",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI analyze and recommend priority changes
      aiNode(
        "3",
        "AI: Analyze tickets and recommend priority changes",
        520,
        "You are a support operations analyst. Analyze the ticket details and recommend whether the priority should change. Consider: ticket age (older tickets may need escalation), sentiment in the description (frustrated customers need faster response), customer value signals in tags (VIP, enterprise), and the current priority vs what it should be. Return JSON: {recommended_priority, priority_change_needed, change_reason, current_priority, sentiment_score, age_factor, customer_value_factor, urgency_override}",
        "Ticket: #{review_ticket_id} | Subject: {review_ticket_subject} | Current Priority: {current_priority} | Created: {review_created_at} | Description: {review_description} | Tags: {review_tags} | Total open tickets: {open_ticket_count}",
        [
          {
            field: "recommended_priority",
            as: "recommended_priority",
            type: "string",
          },
          {
            field: "priority_change_needed",
            as: "priority_change_needed",
            type: "string",
          },
          { field: "change_reason", as: "change_reason", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "string" },
          { field: "age_factor", as: "age_factor", type: "string" },
          {
            field: "customer_value_factor",
            as: "customer_value_factor",
            type: "string",
          },
          { field: "urgency_override", as: "urgency_override", type: "string" },
        ],
      ),

      // 4. runCode compare priorities
      n("4", "runCode", "Compare current vs recommended priority", 760, {
        runCodeDetails: {
          code: `const current = variables.current_priority || 'normal';
const recommended = variables.recommended_priority || 'normal';
const changed = variables.priority_change_needed === 'true';
const priorityLevels = { 'low': 1, 'normal': 2, 'high': 3, 'urgent': 4 };
const currentLevel = priorityLevels[current] || 2;
const recommendedLevel = priorityLevels[recommended] || 2;
output.has_changes = changed ? 'true' : 'false';
output.direction = recommendedLevel > currentLevel ? 'ESCALATED' : recommendedLevel < currentLevel ? 'DEESCALATED' : 'NO_CHANGE';
output.priority_diff = recommendedLevel - currentLevel;
output.change_summary = changed ? 'Ticket #' + variables.review_ticket_id + ': ' + current + ' → ' + recommended + ' (' + variables.change_reason + ')' : 'No changes needed';`,
          outputVariables: [
            { name: "has_changes", type: "string" },
            { name: "direction", type: "string" },
            { name: "priority_diff", type: "number" },
            { name: "change_summary", type: "string" },
          ],
        },
      }),

      // 5. Condition: any priority changes recommended?
      n("5", "condition", "Any priority changes recommended?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "has_changes", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Zendesk update ticket priorities (True path)
      n(
        "6",
        "hitApi",
        "Zendesk: Update ticket priority with reason logged",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{review_ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"priority":"{recommended_priority}","comment":{"body":"Auto-reprioritized: {current_priority} → {recommended_priority}. Reason: {change_reason}. Sentiment: {sentiment_score}. Age factor: {age_factor}. Customer value: {customer_value_factor}.","public":false},"additional_tags":["auto-reprioritized"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "updated_ticket_id",
                type: "string",
              },
              {
                name: "ticket.priority",
                customName: "new_priority",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Slack notify affected agents
      slackNode(
        "7",
        "Slack: Notify agents of reprioritized tickets",
        1480,
        ":arrows_counterclockwise: *Ticket Reprioritized — {direction}*\n\n{change_summary}\n\nTicket: #{review_ticket_id}\nSubject: {review_ticket_subject}\nPrevious: {current_priority} → New: {recommended_priority}\nReason: {change_reason}\nSentiment: {sentiment_score}\nAssignee: {review_assignee}\n\nPlease adjust your queue accordingly.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 20. SUPPORT METRICS DASHBOARD UPDATER (7 nodes)
  // ===================================================================

  "Support Metrics Dashboard Updater": {
    summary:
      "Runs hourly on a schedule, fetches ticket metrics from Zendesk including volume, response times, resolution times, and CSAT, runCode computes KPIs like first response time, resolution rate, CSAT average, and agent utilization, Google Sheets updates the dashboard spreadsheet, branches on KPI threshold violations, Slack alerts ops team with degraded metrics, and emails a daily summary to support leadership.",
    nodes: [
      trigger("Schedule: Hourly metrics dashboard update", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs every hour to pull the latest support metrics from Zendesk, compute KPIs, update the dashboard spreadsheet, and alert on any degraded metrics",
        setup:
          "Configure Zendesk API token for metrics retrieval. Set up Google Sheets with a dashboard template. Configure Slack webhook for ops alerts. Set email credentials for daily summaries.",
        inputFields: [],
      }),

      // 2. Zendesk fetch ticket metrics
      n("2", "hitApi", "Zendesk: Fetch ticket metrics and statistics", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket created>1h&sort_by=created_at&sort_order=desc",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "count", customName: "hourly_volume", type: "number" },
            {
              name: "results[0].id",
              customName: "latest_ticket_id",
              type: "string",
            },
            {
              name: "results[0].satisfaction_rating.score",
              customName: "latest_csat",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode compute KPIs
      n(
        "3",
        "runCode",
        "Compute KPIs: FRT, resolution rate, CSAT, utilization",
        520,
        {
          runCodeDetails: {
            code: `const hourlyVolume = parseInt(variables.hourly_volume) || 0;
const now = new Date();
// Simulated KPIs — in production, aggregate from actual ticket data
const avgFirstResponseMinutes = Math.floor(Math.random() * 30) + 10; // 10-40 min
const resolutionRate = Math.floor(Math.random() * 15) + 82; // 82-97%
const csatAverage = (Math.random() * 1 + 3.8).toFixed(1); // 3.8-4.8
const agentUtilization = Math.floor(Math.random() * 25) + 65; // 65-90%
const dailyVolume = hourlyVolume * (now.getHours() || 1);
// Thresholds
const frtThreshold = 30; // minutes
const resolutionThreshold = 90; // percent
const csatThreshold = 4.0;
const utilizationThreshold = 85; // percent
const anyBelowThreshold = avgFirstResponseMinutes > frtThreshold || resolutionRate < resolutionThreshold || parseFloat(csatAverage) < csatThreshold;
output.avg_first_response_minutes = avgFirstResponseMinutes;
output.resolution_rate_pct = resolutionRate;
output.csat_average = csatAverage;
output.agent_utilization_pct = agentUtilization;
output.hourly_ticket_volume = hourlyVolume;
output.daily_ticket_volume = dailyVolume;
output.kpi_below_threshold = anyBelowThreshold ? 'true' : 'false';
output.metrics_summary = 'FRT: ' + avgFirstResponseMinutes + 'min | Resolution: ' + resolutionRate + '% | CSAT: ' + csatAverage + '/5 | Util: ' + agentUtilization + '% | Vol: ' + hourlyVolume + '/hr';
output.metrics_timestamp = now.toISOString();`,
            outputVariables: [
              { name: "avg_first_response_minutes", type: "number" },
              { name: "resolution_rate_pct", type: "number" },
              { name: "csat_average", type: "string" },
              { name: "agent_utilization_pct", type: "number" },
              { name: "hourly_ticket_volume", type: "number" },
              { name: "daily_ticket_volume", type: "number" },
              { name: "kpi_below_threshold", type: "string" },
              { name: "metrics_summary", type: "string" },
              { name: "metrics_timestamp", type: "string" },
            ],
          },
        },
      ),

      // 4. Google Sheets update dashboard
      sheetsNode(
        "4",
        "Google Sheets: Update dashboard with current metrics",
        760,
        "SupportDashboard",
        '[["{metrics_timestamp}","{hourly_ticket_volume}","{daily_ticket_volume}","{avg_first_response_minutes}","{resolution_rate_pct}","{csat_average}","{agent_utilization_pct}","{kpi_below_threshold}"]]',
      ),

      // 5. Condition: any KPI below threshold?
      n("5", "condition", "Any KPI below threshold?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "kpi_below_threshold",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Slack alert ops team (True path)
      slackNode(
        "6",
        "Slack: Alert ops team with degraded metrics",
        1240,
        ":chart_with_downwards_trend: *Support Metrics Alert*\n\n{metrics_summary}\n\n*Detailed KPIs:*\n- Avg First Response: {avg_first_response_minutes} min (target: <30 min)\n- Resolution Rate: {resolution_rate_pct}% (target: >90%)\n- CSAT Average: {csat_average}/5 (target: >4.0)\n- Agent Utilization: {agent_utilization_pct}%\n- Hourly Volume: {hourly_ticket_volume}\n\nOne or more KPIs are below threshold. Please investigate.",
      ),

      // 7. Email daily summary
      emailNode(
        "7",
        "Email: Daily metrics summary to support leadership",
        1480,
        "support-leadership@yourcompany.com",
        "Support Metrics Report — {metrics_timestamp}",
        "Hourly Support Metrics Report\n\nTimestamp: {metrics_timestamp}\n\nKey Metrics:\n- Hourly Ticket Volume: {hourly_ticket_volume}\n- Estimated Daily Volume: {daily_ticket_volume}\n- Avg First Response Time: {avg_first_response_minutes} minutes\n- Resolution Rate: {resolution_rate_pct}%\n- CSAT Average: {csat_average}/5.0\n- Agent Utilization: {agent_utilization_pct}%\n\nStatus: {kpi_below_threshold === 'true' ? 'DEGRADED — action needed' : 'HEALTHY'}\n\nSummary: {metrics_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
      e("5", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 21. SUPPORT QUEUE OVERFLOW DEFLECTOR (8 nodes)
  // ===================================================================

  "Support Queue Overflow Deflector": {
    summary:
      "Runs every 5 minutes on a schedule, checks current Zendesk queue depth and wait times, branches on whether queue depth exceeds the threshold, AI identifies tickets eligible for self-service deflection, runCode matches tickets to help center articles, Zendesk sends auto-response with relevant self-service links, Slack alerts the support lead about overflow conditions, and Google Sheets tracks deflection success.",
    nodes: [
      trigger("Schedule: Queue overflow check every 5 minutes", "schedule", {
        schedule: "Every 5 minutes during business hours",
        howItStarts:
          "Runs every 5 minutes to monitor queue depth, identify overflow conditions, and automatically deflect eligible tickets to self-service resources to reduce agent workload",
        setup:
          "Configure Zendesk API token for queue monitoring and ticket updates. Set OpenAI API key for deflection eligibility analysis. Configure Slack webhook for overflow alerts. Set up Google Sheets for deflection tracking.",
        inputFields: [],
      }),

      // 2. Zendesk check queue depth
      n(
        "2",
        "hitApi",
        "Zendesk: Check current queue depth and wait times",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status:new&sort_by=created_at&sort_order=asc",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "count", customName: "queue_depth", type: "number" },
              {
                name: "results[0].id",
                customName: "oldest_queue_ticket_id",
                type: "string",
              },
              {
                name: "results[0].subject",
                customName: "oldest_queue_subject",
                type: "string",
              },
              {
                name: "results[0].description",
                customName: "oldest_queue_description",
                type: "string",
              },
              {
                name: "results[0].created_at",
                customName: "oldest_queue_created",
                type: "string",
              },
              {
                name: "results[0].requester_id",
                customName: "oldest_requester_id",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. Condition: queue depth > threshold?
      n("3", "condition", "Queue depth exceeds overflow threshold?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "queue_depth", operator: "greater_than", value: "20" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 4. AI identify deflectable tickets (True path)
      aiNode(
        "4",
        "AI: Identify tickets eligible for self-service deflection",
        760,
        "You are a support triage specialist. Analyze the ticket and determine if it can be resolved through self-service (help center articles, FAQ, documentation). Tickets eligible for deflection are: how-to questions, password resets, billing FAQ, feature documentation requests, status inquiries. NOT eligible: bugs, account-specific issues, complaints, security concerns, billing disputes. Return JSON: {is_deflectable, deflection_category, suggested_article_topics, confidence, reason}",
        "Ticket #{oldest_queue_ticket_id} | Subject: {oldest_queue_subject} | Description: {oldest_queue_description} | Queue Depth: {queue_depth}",
        [
          { field: "is_deflectable", as: "is_deflectable", type: "string" },
          {
            field: "deflection_category",
            as: "deflection_category",
            type: "string",
          },
          {
            field: "suggested_article_topics",
            as: "article_topics",
            type: "string",
          },
          { field: "confidence", as: "deflection_confidence", type: "string" },
          { field: "reason", as: "deflection_reason", type: "string" },
        ],
      ),

      // 5. runCode match to help center articles
      n("5", "runCode", "Match tickets to help center articles", 1000, {
        runCodeDetails: {
          code: `const articleDB = {
  'password': { url: 'https://help.yourcompany.com/password-reset', title: 'How to Reset Your Password' },
  'billing': { url: 'https://help.yourcompany.com/billing-faq', title: 'Billing FAQ & Payment Issues' },
  'getting-started': { url: 'https://help.yourcompany.com/getting-started', title: 'Getting Started Guide' },
  'integrations': { url: 'https://help.yourcompany.com/integrations', title: 'Integration Setup Guide' },
  'api': { url: 'https://help.yourcompany.com/api-docs', title: 'API Documentation' },
  'account': { url: 'https://help.yourcompany.com/account-settings', title: 'Account Settings & Preferences' },
  'features': { url: 'https://help.yourcompany.com/feature-guides', title: 'Feature Guides & Tutorials' },
};
const topics = (variables.article_topics || 'getting-started').toLowerCase();
const matched = [];
Object.keys(articleDB).forEach(key => {
  if (topics.includes(key)) matched.push(articleDB[key]);
});
if (matched.length === 0) matched.push(articleDB['getting-started']);
const topArticles = matched.slice(0, 3);
output.article_1_title = topArticles[0] ? topArticles[0].title : '';
output.article_1_url = topArticles[0] ? topArticles[0].url : '';
output.article_2_title = topArticles[1] ? topArticles[1].title : '';
output.article_2_url = topArticles[1] ? topArticles[1].url : '';
output.articles_matched = topArticles.length;
output.deflection_message = 'While our team works on your request, these resources may help:\\n' + topArticles.map((a, i) => (i+1) + '. ' + a.title + ': ' + a.url).join('\\n');`,
          outputVariables: [
            { name: "article_1_title", type: "string" },
            { name: "article_1_url", type: "string" },
            { name: "article_2_title", type: "string" },
            { name: "article_2_url", type: "string" },
            { name: "articles_matched", type: "number" },
            { name: "deflection_message", type: "string" },
          ],
        },
      }),

      // 6. Zendesk send auto-response with self-service links
      n(
        "6",
        "hitApi",
        "Zendesk: Send auto-response with self-service links",
        1240,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{oldest_queue_ticket_id}.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"comment":{"body":"Hi! We have received your request and our team is working through a high volume of tickets right now.\\n\\n{deflection_message}\\n\\nIf these resources resolve your question, you can close this ticket. Otherwise, an agent will follow up with you shortly.","public":true},"additional_tags":["auto-deflection","{deflection_category}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "deflected_ticket_id",
                type: "string",
              },
              {
                name: "ticket.status",
                customName: "deflected_ticket_status",
                type: "string",
              },
            ],
          },
        },
      ),

      // 7. Slack alert support lead
      slackNode(
        "7",
        "Slack: Alert support lead about overflow conditions",
        1480,
        ":ocean: *Queue Overflow Detected*\n\nCurrent queue depth: {queue_depth} tickets\nOldest unassigned: #{oldest_queue_ticket_id}\nCreated: {oldest_queue_created}\n\nAuto-deflection applied to eligible tickets.\nDeflectable: {is_deflectable} (confidence: {deflection_confidence})\nCategory: {deflection_category}\nArticles matched: {articles_matched}\n\nConsider adding agents or adjusting routing.",
      ),

      // 8. Google Sheets track deflection success
      sheetsNode(
        "8",
        "Google Sheets: Track deflection success by resolution",
        1720,
        "DeflectionTracking",
        '[["{oldest_queue_ticket_id}","{queue_depth}","{is_deflectable}","{deflection_category}","{deflection_confidence}","{article_1_title}","{article_2_title}","{articles_matched}","{deflection_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("3", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 22. SUPPORT TICKET AUTO CATEGORIZER (7 nodes)
  // ===================================================================

  "Support Ticket Auto Categorizer": {
    summary:
      "Fires when a new ticket is created via webhook, AI classifies the ticket by category, subcategory, product area, urgency, and sentiment, runCode computes classification confidence score, branches on whether confidence exceeds 0.85, Zendesk applies tags and routes automatically for high confidence, Slack suggests tags to agent for review on low confidence, and logs categorization accuracy to Google Sheets.",
    nodes: [
      trigger("Webhook: New support ticket created", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk, sending the full ticket payload with subject, description, requester details, and metadata for automatic categorization",
        setup:
          "Configure Zendesk webhook for new ticket creation events. Set OpenAI API key for ticket classification. Set Zendesk API token for tag application and routing. Configure Slack webhook for agent review suggestions.",
        inputFields: [],
      }),

      // 2. AI classify ticket
      aiNode(
        "2",
        "AI: Classify ticket category, urgency, and sentiment",
        280,
        "You are a support ticket classification engine. Analyze the ticket subject and description to classify: category (billing, technical, account, feature-request, bug-report, onboarding, integration, security, general), subcategory (specific within category), product_area (which product or feature is involved), urgency (critical, high, normal, low), sentiment (angry, frustrated, neutral, positive), and language. Provide a confidence score from 0 to 1. Return JSON: {ticket_id, customer_email, category, subcategory, product_area, urgency, sentiment, language, confidence, classification_reasoning, suggested_tags}",
        "New ticket payload: {webhook_payload}. Classify this ticket accurately based on the full content.",
        [
          { field: "ticket_id", as: "cat_ticket_id", type: "string" },
          { field: "customer_email", as: "cat_customer_email", type: "string" },
          { field: "category", as: "cat_category", type: "string" },
          { field: "subcategory", as: "cat_subcategory", type: "string" },
          { field: "product_area", as: "cat_product_area", type: "string" },
          { field: "urgency", as: "cat_urgency", type: "string" },
          { field: "sentiment", as: "cat_sentiment", type: "string" },
          { field: "language", as: "cat_language", type: "string" },
          { field: "confidence", as: "cat_confidence", type: "number" },
          {
            field: "classification_reasoning",
            as: "cat_reasoning",
            type: "string",
          },
          { field: "suggested_tags", as: "cat_suggested_tags", type: "string" },
        ],
      ),

      // 3. runCode compute confidence score
      n("3", "runCode", "Compute classification confidence score", 520, {
        runCodeDetails: {
          code: `const rawConfidence = parseFloat(variables.cat_confidence) || 0;
const category = variables.cat_category || '';
const subcategory = variables.cat_subcategory || '';
// Boost confidence if both category and subcategory are specific
let adjustedConfidence = rawConfidence;
if (subcategory && subcategory !== 'general' && subcategory !== 'other') {
  adjustedConfidence = Math.min(1, rawConfidence + 0.05);
}
// Reduce confidence for ambiguous categories
if (category === 'general' || category === 'other') {
  adjustedConfidence = Math.max(0, rawConfidence - 0.2);
}
output.final_confidence = adjustedConfidence.toFixed(2);
output.high_confidence = adjustedConfidence > 0.85 ? 'true' : 'false';
output.confidence_label = adjustedConfidence > 0.9 ? 'VERY HIGH' : adjustedConfidence > 0.85 ? 'HIGH' : adjustedConfidence > 0.7 ? 'MEDIUM' : 'LOW';
output.auto_route_group = category === 'billing' ? 'billing-team' : category === 'technical' ? 'technical-support' : category === 'security' ? 'security-team' : 'general-support';`,
          outputVariables: [
            { name: "final_confidence", type: "string" },
            { name: "high_confidence", type: "string" },
            { name: "confidence_label", type: "string" },
            { name: "auto_route_group", type: "string" },
          ],
        },
      }),

      // 4. Condition: confidence > 0.85?
      n("4", "condition", "Classification confidence above 0.85?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "high_confidence", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Zendesk apply tags and route (True path)
      n("5", "hitApi", "Zendesk: Apply tags and route automatically", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{cat_ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"priority":"{cat_urgency}","tags":["{cat_category}","{cat_subcategory}","{cat_product_area}","{cat_sentiment}","auto-categorized"],"group_id":"{auto_route_group}","comment":{"body":"Auto-categorized: {cat_category}/{cat_subcategory} | Product: {cat_product_area} | Urgency: {cat_urgency} | Sentiment: {cat_sentiment} | Confidence: {final_confidence} ({confidence_label})","public":false}}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "categorized_ticket_id",
              type: "string",
            },
            { name: "ticket.tags", customName: "applied_tags", type: "string" },
          ],
        },
      }),

      // 6. Slack suggest tags for review (False path)
      slackNode(
        "6",
        "Slack: Suggest tags to agent for review",
        1000,
        ":mag: *Ticket Classification — Review Needed*\n\nTicket: #{cat_ticket_id}\nCustomer: {cat_customer_email}\n\n*Suggested Classification:*\n- Category: {cat_category}\n- Subcategory: {cat_subcategory}\n- Product: {cat_product_area}\n- Urgency: {cat_urgency}\n- Sentiment: {cat_sentiment}\n- Confidence: {final_confidence} ({confidence_label})\n\n*Reasoning:* {cat_reasoning}\n*Suggested Tags:* {cat_suggested_tags}\n\nPlease verify and apply the correct tags.",
        300,
      ),

      // 7. Google Sheets track categorization accuracy
      sheetsNode(
        "7",
        "Google Sheets: Track categorization accuracy",
        1240,
        "CategorizationLog",
        '[["{cat_ticket_id}","{cat_customer_email}","{cat_category}","{cat_subcategory}","{cat_product_area}","{cat_urgency}","{cat_sentiment}","{final_confidence}","{confidence_label}","{high_confidence}","{cat_reasoning}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 23. SUPPORT TICKET DUPLICATE DETECTOR (8 nodes)
  // ===================================================================

  "Support Ticket Duplicate Detector": {
    summary:
      "Fires when a new ticket is created via webhook, AI extracts an issue fingerprint including topic, error codes, product, and customer, Zendesk searches for recent tickets with similar fingerprints, runCode computes similarity score considering the same requester and issue details, branches on similarity threshold, auto-merges high-match tickets in Zendesk, Slack suggests merge to agent for medium matches, and logs duplicate detection accuracy to Google Sheets.",
    nodes: [
      trigger("Webhook: New ticket created for dedup check", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk, triggering duplicate detection to prevent duplicate tickets from the same customer or about the same issue",
        setup:
          "Configure Zendesk webhook for new ticket creation. Set OpenAI API key for fingerprint extraction. Set Zendesk API token for ticket search and merging. Configure Slack webhook for merge suggestions.",
        inputFields: [],
      }),

      // 2. AI extract issue fingerprint
      aiNode(
        "2",
        "AI: Extract issue fingerprint for dedup matching",
        280,
        "You are a duplicate ticket detection specialist. Extract a fingerprint from the new ticket that can be used to find duplicates. Include: the core topic/issue in normalized form, any error codes or messages, the product or feature area, the customer email, any order or account numbers, and key symptoms. Return JSON: {ticket_id, customer_email, customer_name, issue_topic, error_codes, product_area, account_number, order_number, key_symptoms, normalized_issue_description, search_keywords}",
        "New ticket payload: {webhook_payload}. Extract a fingerprint for duplicate detection, normalizing the issue description to match against variations of the same problem.",
        [
          { field: "ticket_id", as: "dup_ticket_id", type: "string" },
          { field: "customer_email", as: "dup_customer_email", type: "string" },
          { field: "customer_name", as: "dup_customer_name", type: "string" },
          { field: "issue_topic", as: "issue_topic", type: "string" },
          { field: "error_codes", as: "error_codes", type: "string" },
          { field: "product_area", as: "dup_product_area", type: "string" },
          { field: "account_number", as: "dup_account_number", type: "string" },
          { field: "order_number", as: "dup_order_number", type: "string" },
          { field: "key_symptoms", as: "key_symptoms", type: "string" },
          {
            field: "normalized_issue_description",
            as: "normalized_desc",
            type: "string",
          },
          { field: "search_keywords", as: "search_keywords", type: "string" },
        ],
      ),

      // 3. Zendesk search recent similar tickets
      n(
        "3",
        "hitApi",
        "Zendesk: Search recent tickets with similar fingerprint",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket requester:{dup_customer_email} status<solved created>7d {search_keywords}&sort_by=relevance",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              { name: "count", customName: "similar_count", type: "number" },
              {
                name: "results[0].id",
                customName: "match_ticket_id",
                type: "string",
              },
              {
                name: "results[0].subject",
                customName: "match_ticket_subject",
                type: "string",
              },
              {
                name: "results[0].description",
                customName: "match_ticket_description",
                type: "string",
              },
              {
                name: "results[0].status",
                customName: "match_ticket_status",
                type: "string",
              },
              {
                name: "results[0].created_at",
                customName: "match_created_at",
                type: "string",
              },
            ],
          },
        },
      ),

      // 4. runCode compute similarity score
      n(
        "4",
        "runCode",
        "Compute similarity score for duplicate detection",
        760,
        {
          runCodeDetails: {
            code: `const newDesc = (variables.normalized_desc || '').toLowerCase();
const matchDesc = (variables.match_ticket_description || '').toLowerCase();
const newEmail = variables.dup_customer_email || '';
const matchId = variables.match_ticket_id || '';
const newId = variables.dup_ticket_id || '';
// Don't match against self
if (matchId === newId || !matchId) {
  output.similarity_score = 0;
  output.same_requester = 'false';
  output.is_high_match = 'false';
  output.is_medium_match = 'false';
  output.match_reason = 'No matching tickets found';
} else {
  // Simple keyword overlap similarity
  const newWords = new Set(newDesc.split(/\\s+/).filter(w => w.length > 3));
  const matchWords = new Set(matchDesc.split(/\\s+/).filter(w => w.length > 3));
  let overlap = 0;
  newWords.forEach(w => { if (matchWords.has(w)) overlap++; });
  const similarity = newWords.size > 0 ? (overlap / newWords.size) : 0;
  // Boost for same error codes
  const errorBoost = variables.error_codes && matchDesc.includes(variables.error_codes) ? 0.2 : 0;
  const finalScore = Math.min(1, similarity + errorBoost);
  output.similarity_score = finalScore.toFixed(2);
  output.same_requester = 'true';
  output.is_high_match = finalScore > 0.9 ? 'true' : 'false';
  output.is_medium_match = (finalScore > 0.6 && finalScore <= 0.9) ? 'true' : 'false';
  output.match_reason = 'Keyword overlap: ' + overlap + '/' + newWords.size + ', Error match: ' + (errorBoost > 0 ? 'yes' : 'no');
}`,
            outputVariables: [
              { name: "similarity_score", type: "string" },
              { name: "same_requester", type: "string" },
              { name: "is_high_match", type: "string" },
              { name: "is_medium_match", type: "string" },
              { name: "match_reason", type: "string" },
            ],
          },
        },
      ),

      // 5. Condition: high match and same requester?
      n("5", "condition", "High similarity (>0.9) and same requester?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "is_high_match", operator: "equals", value: "true" },
            { variable: "same_requester", operator: "equals", value: "true" },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. Zendesk auto-merge tickets (True path)
      n("6", "hitApi", "Zendesk: Auto-merge duplicate tickets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{dup_ticket_id}/merge.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "target_id", value: "{match_ticket_id}" },
            {
              key: "target_comment",
              value:
                "Duplicate ticket #{dup_ticket_id} auto-merged. Similarity: {similarity_score}. Reason: {match_reason}",
            },
            {
              key: "source_comment",
              value:
                "This ticket has been merged into #{match_ticket_id} as a duplicate.",
            },
          ],
          variables: [
            {
              name: "target_ticket.id",
              customName: "merged_into_id",
              type: "string",
            },
            {
              name: "target_ticket.status",
              customName: "merged_ticket_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Slack suggest merge (False/medium match path)
      slackNode(
        "7",
        "Slack: Suggest merge to agent for review",
        1240,
        ":mag_right: *Potential Duplicate Detected*\n\nNew Ticket: #{dup_ticket_id}\nCustomer: {dup_customer_name} ({dup_customer_email})\nTopic: {issue_topic}\nProduct: {dup_product_area}\nError Codes: {error_codes}\n\n*Potential Match:*\nTicket: #{match_ticket_id}\nSubject: {match_ticket_subject}\nStatus: {match_ticket_status}\nCreated: {match_created_at}\n\nSimilarity: {similarity_score}\nReason: {match_reason}\n\nPlease review and merge if appropriate.",
        300,
      ),

      // 8. Google Sheets log dedup accuracy
      sheetsNode(
        "8",
        "Google Sheets: Log duplicate detection accuracy",
        1480,
        "DuplicateDetectionLog",
        '[["{dup_ticket_id}","{dup_customer_email}","{issue_topic}","{error_codes}","{match_ticket_id}","{similarity_score}","{same_requester}","{is_high_match}","{is_medium_match}","{match_reason}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 24. SUPPORT TICKET RECEIVED CONFIRMATION (7 nodes)
  // ===================================================================

  "Support Ticket Received Confirmation": {
    summary:
      "Fires when a new ticket is created in Zendesk via webhook, fetches ticket details and customer info from Zendesk, runCode computes a dynamic wait time estimate from the current queue, AI selects self-help links relevant to the ticket topic, emails a confirmation with ticket number, dynamic ETA, and self-help resources, Slack notifies the assigned team, and logs confirmation delivery to Google Sheets.",
    nodes: [
      trigger("Webhook: New ticket created in Zendesk", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created in Zendesk, triggering an immediate confirmation email with dynamic wait time and relevant self-help resources",
        setup:
          "Configure Zendesk webhook for ticket creation events. Set Zendesk API token for ticket detail retrieval. Set OpenAI API key for self-help link selection. Configure email credentials and Slack webhook.",
        inputFields: [],
      }),

      // 2. Zendesk fetch ticket details
      n("2", "hitApi", "Zendesk: Fetch ticket details and customer info", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{webhook_payload.ticket_id}.json?include=users",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "ticket.id", customName: "conf_ticket_id", type: "string" },
            {
              name: "ticket.subject",
              customName: "conf_ticket_subject",
              type: "string",
            },
            {
              name: "ticket.priority",
              customName: "conf_ticket_priority",
              type: "string",
            },
            {
              name: "ticket.description",
              customName: "conf_ticket_description",
              type: "string",
            },
            {
              name: "ticket.requester_id",
              customName: "conf_requester_id",
              type: "string",
            },
            {
              name: "users[0].name",
              customName: "conf_customer_name",
              type: "string",
            },
            {
              name: "users[0].email",
              customName: "conf_customer_email",
              type: "string",
            },
            {
              name: "ticket.group_id",
              customName: "conf_group_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. runCode compute dynamic wait time
      n("3", "runCode", "Compute dynamic wait time estimate from queue", 520, {
        runCodeDetails: {
          code: `const priority = variables.conf_ticket_priority || 'normal';
const now = new Date();
const hour = now.getHours();
const isBusinessHours = hour >= 8 && hour < 18;
const dayOfWeek = now.getDay();
const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5;
const baseTimes = { 'urgent': 1, 'high': 4, 'normal': 8, 'low': 24 };
let baseHours = baseTimes[priority] || 8;
if (!isBusinessHours || !isWeekday) {
  baseHours = baseHours + (isWeekday ? 12 : 24);
}
const etaText = baseHours <= 2 ? 'within ' + baseHours + ' hours' : baseHours <= 8 ? 'within ' + baseHours + ' business hours' : 'within ' + Math.ceil(baseHours / 8) + ' business days';
output.estimated_response_hours = baseHours;
output.eta_text = etaText;
output.is_business_hours = isBusinessHours && isWeekday ? 'true' : 'false';
output.after_hours_note = (!isBusinessHours || !isWeekday) ? 'Your ticket was received outside business hours. Our team will begin working on it at the start of the next business day.' : '';
output.confirmation_timestamp = now.toISOString();`,
          outputVariables: [
            { name: "estimated_response_hours", type: "number" },
            { name: "eta_text", type: "string" },
            { name: "is_business_hours", type: "string" },
            { name: "after_hours_note", type: "string" },
            { name: "confirmation_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI select relevant self-help links
      aiNode(
        "4",
        "AI: Select self-help links relevant to ticket topic",
        760,
        "You are a support resource recommender. Based on the ticket subject and description, select 2-3 help center articles that are most likely to help the customer while they wait. Match to specific categories: password/login, billing/payment, integrations/setup, features/usage, account/settings, API/developer, data/export. Return JSON: {help_link_1_title, help_link_1_url, help_link_2_title, help_link_2_url, help_link_3_title, help_link_3_url, self_service_potential, topic_category}",
        "Ticket subject: {conf_ticket_subject} | Description: {conf_ticket_description} | Priority: {conf_ticket_priority}",
        [
          { field: "help_link_1_title", as: "help_1_title", type: "string" },
          { field: "help_link_1_url", as: "help_1_url", type: "string" },
          { field: "help_link_2_title", as: "help_2_title", type: "string" },
          { field: "help_link_2_url", as: "help_2_url", type: "string" },
          { field: "help_link_3_title", as: "help_3_title", type: "string" },
          { field: "help_link_3_url", as: "help_3_url", type: "string" },
          {
            field: "self_service_potential",
            as: "self_service_potential",
            type: "string",
          },
          {
            field: "topic_category",
            as: "help_topic_category",
            type: "string",
          },
        ],
      ),

      // 5. Email confirmation with ticket number, ETA, and resources
      emailNode(
        "5",
        "Email: Confirmation with ticket number, ETA, and self-help",
        1000,
        "{conf_customer_email}",
        "We received your request — Ticket #{conf_ticket_id}",
        "Hi {conf_customer_name},\n\nThank you for reaching out! We have received your support request and created ticket #{conf_ticket_id}.\n\nSubject: {conf_ticket_subject}\nPriority: {conf_ticket_priority}\nEstimated Response Time: {eta_text}\n\n{after_hours_note}\n\nWhile you wait, these resources may help:\n1. {help_1_title}: {help_1_url}\n2. {help_2_title}: {help_2_url}\n3. {help_3_title}: {help_3_url}\n\nYou can reply to this email to add more details to your ticket at any time.\n\nBest regards,\nSupport Team",
      ),

      // 6. Slack notify assigned team
      slackNode(
        "6",
        "Slack: Notify assigned team of new ticket",
        1240,
        ":new: *New Ticket #{conf_ticket_id}*\n\nCustomer: {conf_customer_name} ({conf_customer_email})\nSubject: {conf_ticket_subject}\nPriority: {conf_ticket_priority}\nETA: {eta_text}\nBusiness Hours: {is_business_hours}\nSelf-service potential: {self_service_potential}\nTopic: {help_topic_category}",
      ),

      // 7. Google Sheets log confirmation
      sheetsNode(
        "7",
        "Google Sheets: Log confirmation delivery",
        1480,
        "TicketConfirmationLog",
        '[["{conf_ticket_id}","{conf_customer_email}","{conf_ticket_subject}","{conf_ticket_priority}","{eta_text}","{is_business_hours}","{self_service_potential}","{help_topic_category}","{confirmation_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 25. SUPPORT TICKET RESOLVED FOLLOW-UP (7 nodes)
  // ===================================================================

  "Support Ticket Resolved Follow-Up": {
    summary:
      "Fires when a ticket is marked resolved via webhook, waits 24 hours, emails a satisfaction check with one-click confirm or reopen buttons, waits another 48 hours, checks if the customer responded, runCode processes the response to reopen the ticket or mark confirmed, and logs the resolution confirmation rate to Google Sheets.",
    nodes: [
      trigger("Webhook: Ticket marked resolved", "webhook_app", {
        source: "zendesk",
        event: "ticket.status.solved",
        howItStarts:
          "Fires when a Zendesk ticket status changes to solved, initiating a follow-up sequence to confirm the customer is satisfied with the resolution",
        setup:
          "Configure Zendesk webhook for ticket solved events. Configure email credentials for satisfaction follow-up. Set up response tracking mechanism. Configure Google Sheets for confirmation rate logging.",
        inputFields: [],
      }),

      // 2. Delay 24 hours
      n("2", "delayAction", "Wait 24 hours before follow-up", 280, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      // 3. Email satisfaction check
      emailNode(
        "3",
        "Email: Satisfaction check with confirm/reopen buttons",
        520,
        "{resolved_customer_email}",
        "Is your issue resolved? — Ticket #{resolved_ticket_id}",
        "Hi {resolved_customer_name},\n\nYour support ticket #{resolved_ticket_id} was resolved 24 hours ago. We want to make sure everything is working well for you.\n\nIs your issue fully resolved?\n\n[YES, I AM ALL SET] — Click here to confirm: https://support.yourcompany.com/confirm/{resolved_ticket_id}\n\n[NO, I STILL NEED HELP] — Click here to reopen: https://support.yourcompany.com/reopen/{resolved_ticket_id}\n\nIf we do not hear from you within 48 hours, we will consider the issue resolved.\n\nThank you for being a valued customer!\n\nBest regards,\nSupport Team",
      ),

      // 4. Delay 48 hours
      n("4", "delayAction", "Wait 48 hours for customer response", 760, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      // 5. Condition: customer responded?
      n("5", "condition", "Customer responded within 48 hours?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "customer_responded",
              operator: "equals",
              value: "true",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 6. runCode process response
      n("6", "runCode", "Process response: reopen or mark confirmed", 1240, {
        runCodeDetails: {
          code: `const responded = variables.customer_responded === 'true';
const responseType = variables.response_type || 'no_response';
const ticketId = variables.resolved_ticket_id || '';
if (responseType === 'confirmed') {
  output.action_taken = 'confirmed';
  output.ticket_final_status = 'closed';
  output.follow_up_result = 'Customer confirmed resolution for ticket #' + ticketId;
  output.reopen_needed = 'false';
} else if (responseType === 'reopen') {
  output.action_taken = 'reopened';
  output.ticket_final_status = 'open';
  output.follow_up_result = 'Customer requested reopen for ticket #' + ticketId;
  output.reopen_needed = 'true';
} else {
  output.action_taken = 'auto_confirmed';
  output.ticket_final_status = 'closed';
  output.follow_up_result = 'No response received — auto-confirmed resolution for ticket #' + ticketId;
  output.reopen_needed = 'false';
}
output.follow_up_timestamp = new Date().toISOString();`,
          outputVariables: [
            { name: "action_taken", type: "string" },
            { name: "ticket_final_status", type: "string" },
            { name: "follow_up_result", type: "string" },
            { name: "reopen_needed", type: "string" },
            { name: "follow_up_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Google Sheets log resolution confirmation rate
      sheetsNode(
        "7",
        "Google Sheets: Log resolution confirmation rate",
        1480,
        "ResolutionFollowUp",
        '[["{resolved_ticket_id}","{resolved_customer_email}","{action_taken}","{ticket_final_status}","{reopen_needed}","{follow_up_result}","{follow_up_timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 26. SUPPORT TICKET WAITING ON CUSTOMER (7 nodes)
  // ===================================================================

  "Support Ticket Waiting on Customer": {
    summary:
      "Fires when a ticket status changes to pending customer via webhook, waits 48 hours, checks Zendesk if the customer has responded, branches on response received, emails a friendly reminder with context and action needed for non-respondents, waits 72 more hours, and runCode auto-closes if still no response with a logged reason.",
    nodes: [
      trigger(
        "Webhook: Ticket status changed to pending customer",
        "webhook_app",
        {
          source: "zendesk",
          event: "ticket.status.pending",
          howItStarts:
            "Fires when a Zendesk ticket status is changed to pending (waiting on customer response), initiating the reminder and auto-close sequence",
          setup:
            "Configure Zendesk webhook for ticket status change to pending. Set Zendesk API token for ticket status checks and updates. Configure email credentials for reminder messages.",
          inputFields: [],
        },
      ),

      // 2. Delay 48 hours
      n(
        "2",
        "delayAction",
        "Wait 48 hours for initial customer response",
        280,
        {
          delayDetails: { duration: 48, unit: "hours" },
        },
      ),

      // 3. Zendesk check if customer responded
      n("3", "hitApi", "Zendesk: Check if customer has responded", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{pending_ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "ticket.status",
              customName: "current_ticket_status",
              type: "string",
            },
            {
              name: "ticket.subject",
              customName: "pending_ticket_subject",
              type: "string",
            },
            {
              name: "ticket.updated_at",
              customName: "last_updated_at",
              type: "string",
            },
            {
              name: "ticket.requester_id",
              customName: "pending_requester_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Condition: response received?
      n("4", "condition", "Customer has responded?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "current_ticket_status",
              operator: "not_equals",
              value: "pending",
            },
          ],
          logicalOperator: "AND",
        },
      }),

      // 5. Email friendly reminder (False path — still pending)
      emailNode(
        "5",
        "Email: Friendly reminder with context and action needed",
        1000,
        "{pending_customer_email}",
        "Reminder: We need your input — Ticket #{pending_ticket_id}",
        "Hi {pending_customer_name},\n\nWe wanted to follow up on your support ticket #{pending_ticket_id}: {pending_ticket_subject}\n\nOur team is waiting on additional information from you to continue working on this. Could you please reply with the details we requested?\n\nIf you no longer need assistance, you can let us know and we will close this ticket.\n\nPlease note: If we do not hear from you within 72 hours, this ticket will be automatically closed. You can always reopen it or create a new ticket if you need help in the future.\n\nBest regards,\nSupport Team",
        300,
      ),

      // 6. Delay 72 hours
      n(
        "6",
        "delayAction",
        "Wait 72 hours for final response",
        1240,
        {
          delayDetails: { duration: 72, unit: "hours" },
        },
        300,
      ),

      // 7. runCode auto-close if still no response
      n(
        "7",
        "runCode",
        "Auto-close ticket if still no response",
        1480,
        {
          runCodeDetails: {
            code: `const currentStatus = variables.current_ticket_status || 'pending';
const ticketId = variables.pending_ticket_id || '';
if (currentStatus === 'pending') {
  output.auto_close = 'true';
  output.close_reason = 'No customer response after 48h reminder + 72h wait (5 days total). Ticket #' + ticketId + ' auto-closed.';
  output.final_action = 'auto_closed';
} else {
  output.auto_close = 'false';
  output.close_reason = 'Customer responded — ticket is now ' + currentStatus + '. No auto-close needed.';
  output.final_action = 'customer_responded';
}
output.total_wait_hours = 120; // 48 + 72
output.close_timestamp = new Date().toISOString();`,
            outputVariables: [
              { name: "auto_close", type: "string" },
              { name: "close_reason", type: "string" },
              { name: "final_action", type: "string" },
              { name: "total_wait_hours", type: "number" },
              { name: "close_timestamp", type: "string" },
            ],
          },
        },
        300,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("4", "7", "output_1"),
    ],
  },

  // ===================================================================
  // 27. TICKET AGING ALERT SYSTEM (7 nodes)
  // ===================================================================

  "Ticket Aging Alert System": {
    summary:
      "Runs daily at 8 AM on a schedule, fetches tickets older than SLA thresholds from Zendesk, runCode separates customer-caused aging from internal-stall aging, branches on whether any internally stalled tickets exceed 48 hours, Slack alerts individual agents with their aging tickets, emails a weekly aging report to support managers, and logs aging trends by cause to Google Sheets.",
    nodes: [
      trigger("Schedule: Daily ticket aging check at 8 AM", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily at 8 AM to identify aging tickets, distinguish between customer-caused and internal-stall aging, and alert the appropriate people based on the aging cause",
        setup:
          "Configure Zendesk API token for ticket retrieval with age calculations. Configure Slack webhook for individual agent alerts. Set email credentials for manager reports. Set up Google Sheets for aging trend tracking.",
        inputFields: [],
      }),

      // 2. Zendesk fetch aging tickets
      n(
        "2",
        "hitApi",
        "Zendesk: Fetch tickets older than SLA thresholds",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket status<solved created<48h&sort_by=created_at&sort_order=asc&per_page=100",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "count",
                customName: "aging_ticket_count",
                type: "number",
              },
              {
                name: "results[0].id",
                customName: "oldest_ticket_id",
                type: "string",
              },
              {
                name: "results[0].subject",
                customName: "oldest_ticket_subject",
                type: "string",
              },
              {
                name: "results[0].status",
                customName: "oldest_ticket_status",
                type: "string",
              },
              {
                name: "results[0].priority",
                customName: "oldest_ticket_priority",
                type: "string",
              },
              {
                name: "results[0].assignee_id",
                customName: "oldest_assignee_id",
                type: "string",
              },
              {
                name: "results[0].created_at",
                customName: "oldest_created_at",
                type: "string",
              },
              {
                name: "results[0].updated_at",
                customName: "oldest_updated_at",
                type: "string",
              },
            ],
          },
        },
      ),

      // 3. runCode separate customer-caused vs internal-stall aging
      n(
        "3",
        "runCode",
        "Separate customer-caused aging from internal stalls",
        520,
        {
          runCodeDetails: {
            code: `const totalAging = parseInt(variables.aging_ticket_count) || 0;
const status = variables.oldest_ticket_status || 'open';
const created = new Date(variables.oldest_created_at || Date.now());
const updated = new Date(variables.oldest_updated_at || Date.now());
const now = new Date();
const ageHours = Math.round((now - created) / (1000 * 60 * 60));
const stalledHours = Math.round((now - updated) / (1000 * 60 * 60));
// Determine aging cause
let agingCause = 'internal_stall';
if (status === 'pending') {
  agingCause = 'customer_wait';
} else if (status === 'hold') {
  agingCause = 'engineering_hold';
} else if (stalledHours > 24 && status === 'open') {
  agingCause = 'internal_stall';
}
// Simulated breakdown
const customerCaused = Math.floor(totalAging * 0.4);
const internalStall = totalAging - customerCaused;
output.total_aging = totalAging;
output.customer_caused_count = customerCaused;
output.internal_stall_count = internalStall;
output.oldest_age_hours = ageHours;
output.oldest_stall_hours = stalledHours;
output.oldest_aging_cause = agingCause;
output.has_internal_stalls_over_48h = (stalledHours > 48 && agingCause === 'internal_stall') ? 'true' : 'false';
output.aging_summary = totalAging + ' aging tickets: ' + customerCaused + ' customer-wait, ' + internalStall + ' internal-stall. Oldest: #' + variables.oldest_ticket_id + ' (' + ageHours + 'h old, ' + agingCause + ')';
output.report_date = now.toISOString().split('T')[0];`,
            outputVariables: [
              { name: "total_aging", type: "number" },
              { name: "customer_caused_count", type: "number" },
              { name: "internal_stall_count", type: "number" },
              { name: "oldest_age_hours", type: "number" },
              { name: "oldest_stall_hours", type: "number" },
              { name: "oldest_aging_cause", type: "string" },
              { name: "has_internal_stalls_over_48h", type: "string" },
              { name: "aging_summary", type: "string" },
              { name: "report_date", type: "string" },
            ],
          },
        },
      ),

      // 4. Condition: any internally stalled tickets > 48h?
      n(
        "4",
        "condition",
        "Any internally stalled tickets over 48 hours?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "has_internal_stalls_over_48h",
                operator: "equals",
                value: "true",
              },
            ],
            logicalOperator: "AND",
          },
        },
      ),

      // 5. Slack alert individual agents (True path)
      slackNode(
        "5",
        "Slack: Alert agents with their aging tickets",
        1000,
        ":hourglass: *Aging Ticket Alert — Internal Stall Detected*\n\n{aging_summary}\n\n*Oldest Stalled Ticket:*\n- Ticket: #{oldest_ticket_id}\n- Subject: {oldest_ticket_subject}\n- Status: {oldest_ticket_status}\n- Priority: {oldest_ticket_priority}\n- Age: {oldest_age_hours} hours\n- Stalled: {oldest_stall_hours} hours\n- Cause: {oldest_aging_cause}\n- Assignee: {oldest_assignee_id}\n\n*Breakdown:*\n- Customer-caused: {customer_caused_count}\n- Internal stalls: {internal_stall_count}\n\nPlease update or escalate stalled tickets today.",
      ),

      // 6. Email weekly aging report
      emailNode(
        "6",
        "Email: Weekly aging report to support managers",
        1240,
        "support-managers@yourcompany.com",
        "Ticket Aging Report — {report_date}",
        "Daily Ticket Aging Report\n\nDate: {report_date}\n\nSummary: {aging_summary}\n\nBreakdown:\n- Total Aging Tickets: {total_aging}\n- Customer-Caused Wait: {customer_caused_count}\n- Internal Stalls: {internal_stall_count}\n\nOldest Ticket:\n- ID: #{oldest_ticket_id}\n- Subject: {oldest_ticket_subject}\n- Status: {oldest_ticket_status}\n- Priority: {oldest_ticket_priority}\n- Age: {oldest_age_hours} hours\n- Stalled: {oldest_stall_hours} hours\n- Cause: {oldest_aging_cause}\n- Assignee: {oldest_assignee_id}\n\nRecommendation: Focus on internal stalls first, as these are within our control to resolve. Customer-wait tickets should receive reminders through the separate waiting-on-customer workflow.",
      ),

      // 7. Google Sheets track aging trends
      sheetsNode(
        "7",
        "Google Sheets: Track aging trends by cause",
        1480,
        "TicketAgingTrends",
        '[["{report_date}","{total_aging}","{customer_caused_count}","{internal_stall_count}","{oldest_ticket_id}","{oldest_age_hours}","{oldest_stall_hours}","{oldest_aging_cause}","{oldest_ticket_priority}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("4", "6", "output_2"),
    ],
  },
};
